import { useState, useEffect, useCallback } from "react";
import {
  Input,
  Switch,
  Dropdown,
  Option,
  makeStyles,
  shorthands,
  TabList,
  Tab,
  Select,
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
  SelectTabEvent,
  SelectTabData,
  useId,
  Button,
  Textarea,
  Dialog,
  DialogTrigger,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogActions,
  DialogContent,
  Avatar,
  Spinner,
  tokens,
  Toaster,
  useToastController,
  Toast,
  ToastTitle,
  ToastBody,
  Link,
} from "@fluentui/react-components";
import FormHeader from "./FormHeader";

import { useLocation, useParams } from "react-router";

import Vertragsaufstellung from "./Vertragsaufstellung";
import { useWindowManager } from "./windowManager/WindowManagerContext";
import { openRiskFormWindow } from "./windowManager/RiskFormWindow";
import { displayContactName } from "../utils/contactDisplay";
import DamagesTableContact from "./DamagesTableContact";
import ContactRisksPanel from "./contactRisks/ContactRisksPanel";
import RelationshipDetails from "./RelationshipDetails";
import {
  apiToForm,
  formToApi,
  type SalutationOption,
  type TitleOption,
  type EmpfehlungResult,
} from "../utils/contactDetailMapper";

// TimelineHistory + VorgaengeHistory imports removed (FE H): both were
// imported here but never rendered. The diary tab uses KundenTagebuch /
// TimelineView instead.
import { ContactVorgaengeTab } from "./ContactVorgaengeTab";
import { TimelineView } from "./TimeLine";
import KundenTagebuch from "./KundenTagebuch";
import MergeFieldArchive from "./MergeFieldArchive";
import { ContactMailView } from "./ContactMailView";
import { openVorgangCreateFlow } from "./vorgang/create/VorgangCreateFlowWindow";

import { DataManagementModal } from "./DataManagementModalNew";
import KennzahlenTab from "./KennzahlenTab";
import { ContactDetailsFormValues, Kontakt } from "../types/Contact";
import {
  Search24Regular,
  Table20Regular,
  LayerDiagonal24Regular,
  Call24Regular,
  MailEdit24Regular,
  ChevronRight20Regular,
  CameraAdd24Regular,
  ChatEmptyRegular,
  Copy24Regular,
  Link24Regular,
  MoreVertical20Regular,
} from "@fluentui/react-icons";
import { SocialMediaIcon } from "./SocialMediaIcon";
import { AvailabilityPopover } from "./AvailabilityPopover";
import { apiUrl } from "../utils/api";
import { fetchLegalFormOptions, type LegalFormOption } from "../utils/legalFormOptions";
import { DocumentsSection } from "./DocumentsSection";
// Outbound calls = app-weiter callNumber()-Helper → In-App-SDK-Widget (ADR 0002).
import { useReferenceData } from "../context/ReferenceDataContext";
import { useBerufsstatusOptions, withBerufsstatusValue } from "../hooks/useBerufsstatusOptions";
import { useTelephony } from "../context/TelephonyContext";
import { FluentTelefonInput } from "./forms/widgets/TelefonEingabe";
import { elementMitId } from "./windowManager/browserFenster/fensterRegister";

type PublicFormType =
  | "contact"
  | "address"
  | "risikoanalyse"
  | "risikoanalyse-step"
  | "risikoanalyse-kurz"
  | "risikoanalyse-kurz-scroll"
  | "hub";

type ActivePublicForm = {
  id: number;
  token: string;
  formType: PublicFormType;
  url: string;
  expiresAt: string | null;
  status: "NOT_FILLED" | "FILLED";
  submitted: boolean;
  canApply: boolean;
};

const PUBLIC_FORM_BRAND = {
  fontFamilyPrimary: "'Barlow', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
  fontFamilyAccent: "'Playfair Display', Georgia, 'Times New Roman', serif",
  colors: {
    pageBg: "#361936",
    primaryPurple: "#4F266B",
    accent: "#F8AA00",
    cardBg: "#fbf9fc",
    muted: "#B4AFB7",
  },
};

// PUBLIC_FORM_PATH_BY_TYPE + mapPublicFormUrlToType +
// detectPublicFormTypeFromUrl removed (FE K). The backend now returns
// the correct URL for each token's actual formType (BE 10 +
// `public-form-url.ts` on the API side), so the client-side rewrite
// is redundant.

const useStyles = makeStyles({
  root: {
    ...shorthands.padding("20px"),
    display: "flex",
    flexDirection: "column",
    gap: "32px",
    position: "relative", // Ensure absolute positioning works relative to this if needed, though we use fixed for FAB
    "@media (max-width: 768px)": {
      padding: "12px",
      // Platz unten, damit der fixe Floating-Action-Button die letzten Felder
      // auf dem Handy nicht verdeckt.
      paddingBottom: "88px",
      gap: "20px",
    },
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "16px 80px",
    alignItems: "start",
    paddingBottom: "20px",
    "@media (max-width: 1024px)": {
      gap: "16px 24px",
    },
    "@media (max-width: 768px)": {
      gridTemplateColumns: "1fr",
      gap: "14px",
      paddingBottom: "12px",
    },
  },
  floatingButton: {
    position: 'fixed',
    bottom: '20px',
    right: '20px',
    backgroundColor: 'rgb(54, 24, 54)',
    color: 'white',
    ...shorthands.borderRadius('50%'),
    width: '60px',
    height: '60px',
    maxWidth: "none",
    maxHeight: "none",
    zIndex: 1000,
    boxShadow: tokens.shadow16,
    ':hover': {
      backgroundColor: 'rgb(80, 40, 80)',
      color: 'white',
    },
    '& svg': {
      width: '48px',
      height: '48px',
    },
    "@media (max-width: 768px)": {
      width: "52px",
      height: "52px",
      right: "14px",
      bottom: "14px",
      '& svg': {
        width: '36px',
        height: '36px',
      },
    },
  },
  labelSection: {
    fontWeight: 600,
    fontSize: "18px",
    gridColumn: "1 / span 2",
    margin: "24px 0 8px 0",
    "@media (max-width: 768px)": {
      gridColumn: "1 / span 1",
      margin: "18px 0 6px 0",
      fontSize: "17px",
    },
  },
  divider: {
    gridColumn: "1 / span 2",
    borderBottom: "2px solid #ddd",
    margin: "16px 0",
    "@media (max-width: 768px)": {
      gridColumn: "1 / span 1",
    },
  },
  fieldRow: {
    display: "grid",
    gridTemplateColumns: "45% 55%",
    alignItems: "center",
    gap: "8px",
    minHeight: "40px",
    "@media (max-width: 768px)": {
      gridTemplateColumns: "1fr",
      alignItems: "stretch",
      gap: "6px",
      minHeight: "unset",
    },
  },
  label: {
    // entferne minWidth/maxWidth, damit es sich in der 45%-Spalte weiten kann:
    textAlign: "left",
    paddingRight: "8px",
    whiteSpace: "nowrap",
    overflow: "hidden",
    fontWeight: 400,
    fontSize: "16px",
    letterSpacing: "1px",
    position: "relative",
    "&::after": {
      content:
        "'........................................................................'",
      left: "100%",
      top: "0%",
      color: "#ccc",
      fontSize: "16px",
      pointerEvents: "none",
      whiteSpace: "nowrap",
      width: "100%",
      overflow: "hidden",
    },
    "@media (max-width: 768px)": {
      fontSize: "14px",
      letterSpacing: "0.2px",
      whiteSpace: "normal",
      paddingRight: 0,
      "&::after": {
        content: '""',
      },
    },
  },
  inputContainer: {
    // sorgt dafür, dass das Input die volle 55%-Spalte nutzt
    width: "100%",
    minWidth: 0,
  },
  stickyBar: {
    position: "sticky",
    top: 0, // ggf. an deine App-Bar Höhe anpassen (z.B. 64)
    zIndex: 100,
    backgroundColor: "#f8f8f8", //gleich wie main Hintergrund
    // leichter Schatten und Unterstrich für Abgrenzung
    //boxShadow: "0 2px 8px rgba(16,24,40,0.06)",
    //borderBottom: "1px solid #eee",
    // Abstand
    paddingTop: "10px",
    paddingBottom: "8px",
    "@media (max-width: 768px)": {
      paddingTop: "4px",
      paddingBottom: "6px",
    },
  },
  publicFormActions: {
    marginBottom: "8px",
  },
  tabsRow: {
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "space-between",
    gap: "12px",
    "@media (max-width: 768px)": {
      alignItems: "stretch",
      flexDirection: "column",
      gap: "8px",
    },
  },
  tabsList: {
    alignItems: "baseline",
    flex: 1,
    minWidth: 0,
    overflowX: "auto",
    overflowY: "hidden",
    scrollbarWidth: "thin",
    "@media (max-width: 768px)": {
      width: "100%",
      flexWrap: "nowrap",
      whiteSpace: "nowrap",
    },
  },
  publicFormMenuButton: {
    color: PUBLIC_FORM_BRAND.colors.primaryPurple,
    minWidth: "30px",
    width: "30px",
    height: "30px",
  },
  publicMenuHeader: {
    color: PUBLIC_FORM_BRAND.colors.pageBg,
    fontWeight: 700,
    fontFamily: PUBLIC_FORM_BRAND.fontFamilyPrimary,
    ...shorthands.borderBottom("1px", "solid", "#E3E0E7"),
    marginBottom: "2px",
    paddingBottom: "8px",
    pointerEvents: "none",
    cursor: "default",
  },
  publicDialogSurface: {
    width: "min(680px, 94vw)",
    ...shorthands.border("1px", "solid", PUBLIC_FORM_BRAND.colors.primaryPurple),
    ...shorthands.borderRadius(tokens.borderRadiusLarge),
    backgroundColor: PUBLIC_FORM_BRAND.colors.pageBg,
    backgroundImage: "linear-gradient(135deg, #361936 0%, #4F266B 100%)",
    boxShadow: tokens.shadow16,
    fontFamily: PUBLIC_FORM_BRAND.fontFamilyPrimary,
  },
  publicDialogTitle: {
    color: PUBLIC_FORM_BRAND.colors.accent,
    fontFamily: PUBLIC_FORM_BRAND.fontFamilyAccent,
    fontWeight: 400,
    fontSize: "clamp(1.2rem, 3vw, 1.9rem)",
  },
  publicDialogContent: {
    display: "grid",
    gap: "10px",
    marginTop: "8px",
  },
  publicDialogLabel: {
    color: "#ffffff",
    fontWeight: 700,
  },
  publicDialogSelectLabel: {
    color: "#ffffff",
    fontWeight: 700,
    marginBottom: "4px",
  },
  publicDialogHint: {
    margin: 0,
    color: "#ffffff",
    fontSize: "0.95rem",
  },
  publicDialogCustomerLabel: {
    marginBottom: "4px",
    color: "#ffffff",
    fontSize: "0.95rem",
    fontWeight: 700,
  },
  publicDialogCustomerInput: {
    width: "100%",
    backgroundColor: "rgba(255, 255, 255, 0.95)",
  },
  publicDialogSelect: {
    width: "100%",
    fontFamily: PUBLIC_FORM_BRAND.fontFamilyPrimary,
    backgroundColor: "rgba(255, 255, 255, 0.95)",
  },
  publicDialogPrimaryButton: {
    backgroundColor: PUBLIC_FORM_BRAND.colors.accent,
    color: PUBLIC_FORM_BRAND.colors.pageBg,
    border: `1px solid ${PUBLIC_FORM_BRAND.colors.accent}`,
    fontFamily: PUBLIC_FORM_BRAND.fontFamilyPrimary,
    fontWeight: 700,
    minWidth: "170px",
    height: "44px",
  },
  publicDialogSecondaryButton: {
    minWidth: "140px",
    height: "44px",
  },
  publicLinkCard: {
    marginTop: "14px",
    marginBottom: "16px",
    ...shorthands.border("1px", "solid", PUBLIC_FORM_BRAND.colors.primaryPurple),
    ...shorthands.borderRadius(tokens.borderRadiusLarge),
    ...shorthands.padding("14px", "14px", "12px", "14px"),
    backgroundColor: PUBLIC_FORM_BRAND.colors.cardBg,
  },
  publicLinkTitle: {
    marginTop: 0,
    marginBottom: "10px",
    color: PUBLIC_FORM_BRAND.colors.pageBg,
    fontFamily: PUBLIC_FORM_BRAND.fontFamilyAccent,
    fontWeight: 400,
    fontSize: "1.2rem",
  },
  publicLinkRow: {
    display: "flex",
    gap: "8px",
    alignItems: "center",
    flexWrap: "wrap",
    "@media (max-width: 768px)": {
      flexDirection: "column",
      alignItems: "stretch",
    },
  },
  // … restliche Styles …
});

// create arrays for the dropdwon for anrede and titel

const familienstandOptions = [
  { value: "Ledig", label: "Ledig" },
  { value: "Verheiratet", label: "Verheiratet" },
  { value: "Geschieden", label: "Geschieden" },
  { value: "Verwitwet", label: "Verwitwet" },
  { value: "Sonstiges", label: "Sonstiges" },
];

// Berufsstatus: siehe hooks/useBerufsstatusOptions (Katalog aus dem Backend).

interface ContactDetailsFormProps {
  initialValues?: ContactDetailsFormValues;
}

export const ContactDetailsForm: React.FC<ContactDetailsFormProps> = ({
  initialValues = {} as ContactDetailsFormValues,
}) => {
  const styles = useStyles();
  const { openWindow } = useWindowManager();
  const { appTeams, users } = useReferenceData();
  const { callNumber } = useTelephony();
  const toasterId = useId("toaster");
  const { dispatchToast } = useToastController(toasterId);
  // Berufsstatus aus dem gepflegten Katalog (ContactBerufsstatus).
  const berufsstatusOptions = useBerufsstatusOptions();

  const handleCall = (number: string) => {
    // Zentraler app-weiter Helper (normalisiert + wählt über das SDK-Widget).
    callNumber(number);
  };

  const handleEmail = (email: string) => {
    if (!email) return;
    window.location.href = `mailto:${email}`;
  };

  const dropdownIdAnrede = useId();
  const dropdownIdTitel = useId();

  const { id } = useParams<{ id: string }>();
  // drin steckt das komplette Kontakt-Objekt, so wie du es in navigate(..., { state: { kontakt: k } }) mitgegeben hast
  const location = useLocation();
  initialValues = location.state?.k || ({} as ContactDetailsFormValues);

  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  // Consolidated state logic with photo handler
  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setUploadingPhoto(true);
      const formData = new FormData();
      formData.append("file", file);

      try {
        const response = await fetch(apiUrl("upload/contact-photo"), {
          method: "POST",
          body: formData,
        });

        if (response.ok) {
          const data = await response.json();
          setContactData((prev) => ({ ...prev, photo_url: data.url }));
          if (id) {
            await fetch(apiUrl(`contact/${id}`), {
              method: 'PUT', // Corrected to PUT as per backend controller
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ ...contactData, photo_url: data.url }) // saving full object
            });
          }
        } else {
          console.error("Upload failed");
        }
      } catch (error) {
        console.error("Error uploading file:", error);
      } finally {
        setUploadingPhoto(false);
      }
    }
  };

  // defaultPhone / defaultBankAccount: initial values + local state for edits
  // Consolidate state
  const [contactData, setContactData] = useState<ContactDetailsFormValues>(() => {
    return initialValues.kontaktId
      ? initialValues
      : ({} as ContactDetailsFormValues);
  });

  // Duplicate /api/contact/:id fetch effect removed (FE H, was at :527-562).
  // A second effect lower in this file already fetches the same endpoint
  // with the comprehensive mapper (relationsSrc flattening + legalForm
  // sync). That one wins the race anyway; this minimal-mapper variant
  // only created a race-condition window where the partial response could
  // overwrite the comprehensive one. See the audit for details.

  const [selectedTab, setSelectedTab] = useState(location.state?.defaultTab || "allgemein");

  // Gesellschaftsform (Label) + Optionen vom Backend
  const [gesellschaftsform, setGesellschaftsform] = useState<string>(
    (typeof initialValues.legalForm === "object" && initialValues.legalForm !== null
      ? ((initialValues.legalForm as any).legalForm ?? "")
      : ((initialValues.legalForm as string) ??
        initialValues.gesellschaftsform ??
        "")) ?? "",
  );

  const [anredeOptions, setAnredeOptions] = useState<SalutationOption[]>([]);
  const [titelOptions, setTitelOptions] = useState<TitleOption[]>([]);
  // `roleOptions` state removed (FE H): declared, initialized to [],
  // never assigned afterward, never consumed.
  const [oldLegalForm, setOldLegalForm] = useState<string>(
    (typeof initialValues.oldLegalForm === "object" && initialValues.oldLegalForm !== null
      ? ((initialValues.oldLegalForm as any).legalForm ?? "")
      : ((initialValues.oldLegalForm as string) ?? "")) ?? "",
  );
  const [legalFormOptions, setLegalFormOptions] = useState<LegalFormOption[]>([]);
  const [legalFormLoading, setLegalFormLoading] = useState(false);

  const [isDataModalOpen, setIsDataModalOpen] = useState(false);
  const [dataModalTab, setDataModalTab] = useState<'addresses' | 'banks' | 'communications'>('addresses');
  const [isNotesModalOpen, setIsNotesModalOpen] = useState(false);
  const [isEmpfehlungModalOpen, setIsEmpfehlungModalOpen] = useState(false);
  const [empfehlungSearch, setEmpfehlungSearch] = useState("");
  const [empfehlungResults, setEmpfehlungResults] = useState<EmpfehlungResult[]>([]);
  const [isEmpfehlungLoading, setIsEmpfehlungLoading] = useState(false);
  const [isFloatingNotesOpen, setIsFloatingNotesOpen] = useState(false);
  const [floatingNotes, setFloatingNotes] = useState('');

  const [activePublicForms, setActivePublicForms] = useState<ActivePublicForm[]>([]);
  const [activePublicFormsKontaktId, setActivePublicFormsKontaktId] = useState<number | null>(null);
  const [selectedPublicFormType, setSelectedPublicFormType] = useState<PublicFormType>("contact");
  const [isPublicFormDialogOpen, setIsPublicFormDialogOpen] = useState(false);
  const [isGeneratingLink, setIsGeneratingLink] = useState(false);
  const [linkError, setLinkError] = useState<string | null>(null);
  const [isCheckingPublicFormStatus, setIsCheckingPublicFormStatus] = useState(false);
  const [applyingFormToken, setApplyingFormToken] = useState<string | null>(null);
  const [revokingFormToken, setRevokingFormToken] = useState<string | null>(null);

  const getKontaktIdForPublicForm = () => {
    const routeId = id ? Number(id) : undefined;
    const stateId = initialValues.kontaktId;
    const dataId = contactData?.kontaktId;
    return stateId || dataId || (Number.isFinite(routeId) ? routeId : undefined);
  };

  const loadActivePublicForms = useCallback(async () => {
    const kontaktId = getKontaktIdForPublicForm();
    if (!kontaktId) return;

    setIsCheckingPublicFormStatus(true);
    try {
      const res = await fetch(apiUrl(`contact/${kontaktId}/public-forms`));
      if (!res.ok) {
        if (res.status === 404) {
          setActivePublicForms([]);
          setActivePublicFormsKontaktId(kontaktId);
          return;
        }
        throw new Error(`Fehler beim Laden der Kundenformulare: ${res.status}`);
      }
      const data = (await res.json()) as Array<{
        id: number;
        token: string;
        formType: string;
        url: string;
        expiresAt: string | null;
        status: "NOT_FILLED" | "FILLED";
        submitted: boolean;
        canApply: boolean;
      }>;
      const mapped: ActivePublicForm[] = data
        .filter(
          (f) =>
            f.formType === "contact" ||
            f.formType === "address" ||
            f.formType === "risikoanalyse" ||
            f.formType === "risikoanalyse-step" ||
            f.formType === "risikoanalyse-kurz" ||
            f.formType === "risikoanalyse-kurz-scroll"
        )
        .map((f) => {
          const formType = f.formType as PublicFormType;
          return {
            id: f.id,
            token: f.token,
            formType,
            url: f.url ?? "",
            expiresAt: f.expiresAt,
            status: f.status,
            submitted: f.submitted,
            canApply: f.canApply,
          };
        });
      setActivePublicForms(mapped);
      setActivePublicFormsKontaktId(kontaktId);
    } catch (e) {
      console.error("Failed to load active public forms", e);
    } finally {
      setIsCheckingPublicFormStatus(false);
    }
  }, [id, initialValues.kontaktId, contactData?.kontaktId]);

  useEffect(() => {
    const kontaktId = getKontaktIdForPublicForm();
    if (!kontaktId || activePublicFormsKontaktId === kontaktId) return;
    setActivePublicForms([]);
  }, [id, initialValues.kontaktId, contactData?.kontaktId, activePublicFormsKontaktId]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLegalFormLoading(true);
        const opts = await fetchLegalFormOptions();
        if (!mounted) return;
        requestAnimationFrame(() => {
          if (mounted) setLegalFormOptions(opts);
        });
      } catch (err) {
        console.error("Failed to load legal forms", err);
        if (mounted) setLegalFormOptions([]);
      } finally {
        if (mounted) setLegalFormLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);


  // Load Salutations and Titles
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const [salRes, titRes] = await Promise.all([
          fetch(apiUrl("contactsalutation")),
          fetch(apiUrl("contact-title"))
        ]);
        if (!mounted) return;
        if (salRes.ok) setAnredeOptions(await salRes.json());
        if (titRes.ok) setTitelOptions(await titRes.json());
      } catch (e) {
        console.error("Failed to load select options", e);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  // Bumped after a relationship is added/removed in RelationshipDetails to
  // force a re-fetch of the contact (and its relationsSrc).
  const [relReload, setRelReload] = useState(0);

  useEffect(() => {
    // Falls ID in URL, lade Daten von dort, falls nicht bereits in initialValues vorhanden (oder erzwinge Reload für Details)
    const kontaktId = id ?? initialValues.kontaktId;
    if (!kontaktId) return;

    // FE H: cancel the in-flight fetch on unmount / id change. Prevents
    // stale-response wins when the user navigates between contacts
    // quickly and prevents "setState on unmounted component" warnings.
    const controller = new AbortController();

    // FE J: opt into the slim shape (BE 7's `?include=`). This file
    // consumes `relationsSrc` directly (for the `relatedCommunications`
    // flattening and the relations tab), so include `relations`. Heavy
    // `events` / `roles` come from the tab-specific children
    // (ContactRisksPanel, ContactVorgaengeTab, etc.) which fetch their
    // own data — we don't need them here.
    fetch(apiUrl(`contact/${kontaktId}`, { include: 'relations' }), {
      signal: controller.signal,
    })
      .then((res) => {
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }
        return res.json();
      })
      .then((data) => {
        // FE L: backend→form mapping lives in `apiToForm` now. Keeps the
        // shadow-field aliasing (`vorname`/`strasse`/…) and the
        // relationsSrc → relatedCommunications flattening in one place.
        const mappedData = apiToForm(data);
        setContactData(mappedData as any);

        // Sync local states
        if (mappedData.legalForm) {
          const lfVal = typeof mappedData.legalForm === 'object' ? mappedData.legalForm.legalForm : mappedData.legalForm;
          setGesellschaftsform(lfVal || "");
        }
        if (mappedData.oldLegalForm) {
          const olfVal = typeof mappedData.oldLegalForm === 'object' ? mappedData.oldLegalForm.legalForm : mappedData.oldLegalForm;
          setOldLegalForm(olfVal || "");
        }
      })
      .catch((err) => {
        if (err?.name === 'AbortError') return; // Expected on unmount / id change.
        console.error("Error loading contact:", err);
        dispatchToast(
          <Toast>
            <ToastTitle>Kontakt konnte nicht geladen werden.</ToastTitle>
            <ToastBody>Bitte Seite neu laden oder später erneut versuchen.</ToastBody>
          </Toast>,
          { intent: "error" },
        );
      });

    return () => controller.abort();
  }, [id, initialValues.kontaktId, relReload]);

  // FE K: public-form status polling with visibility-aware pause + a
  // termination condition for the "all submitted" steady state.
  //
  // Before this commit the effect fired every 15s regardless of whether
  // the tab was hidden or whether anything was still pending. That meant
  // 4 req/min per open contact detail page, indefinitely. Replaced by:
  //
  //   - one initial load on mount
  //   - a 15s interval that only fires while `document.visibilityState`
  //     is 'visible' AND at least one form is NOT yet submitted
  //   - re-arm on visibilitychange so the cadence resumes when the user
  //     comes back to the tab
  //   - explicit dep on `id` / `initialValues.kontaktId` / current
  //     contact id (NOT on the `loadActivePublicForms` callback identity,
  //     which used to reset the interval on every contactData render)
  useEffect(() => {
    const kontaktId = getKontaktIdForPublicForm();
    if (!kontaktId) return;

    loadActivePublicForms();

    const allDone = () =>
      activePublicForms.length > 0 &&
      activePublicForms.every((f) => f.submitted);

    let timer: number | null = null;
    const start = () => {
      if (timer != null) return;
      timer = window.setInterval(() => {
        if (document.visibilityState !== 'visible') return;
        if (allDone()) return;
        loadActivePublicForms();
      }, 15000);
    };
    const stop = () => {
      if (timer != null) {
        window.clearInterval(timer);
        timer = null;
      }
    };

    if (document.visibilityState === 'visible') start();
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        loadActivePublicForms();
        start();
      } else {
        stop();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      stop();
    };
    // Intentionally NOT depending on `loadActivePublicForms` — that
    // callback's identity changes whenever `contactData` is set, which
    // would reset the interval mid-flight.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, initialValues.kontaktId, contactData?.kontaktId]);



  const handleSave = async () => {
    if (!contactData) return;

    // Merge the top-level shadow fields (`strasse/plz/ort/iban/bic/…`,
    // which the inline form bindings still write into) into the default
    // child row. BE 5 made the child rows the source of truth — the
    // synthetic "push a new default row when none exists" path is gone;
    // the DataManagementModal is the only place that creates rows.
    //
    // The `projectDefaults` helper on the backend falls back to the first
    // row when no `isDefault=true` row exists, so we just mirror that
    // contract: find the default (else first), merge shadow fields, leave
    // the rest of the array alone.
    const mergeIntoDefault = <T extends { isDefault?: boolean }>(
      list: T[],
      patch: Partial<T>,
    ): T[] => {
      // FE H footgun fix: if the contact has no children yet but the user
      // filled the inline shadow fields (strasse / iban / etc.), the
      // previous version returned `[]` and the data was silently dropped.
      // Push the patch as a NEW row marked default so the first-save case
      // actually persists.
      const patchHasContent = Object.values(patch).some(
        (v) => v !== undefined && v !== null && v !== '',
      );
      if (list.length === 0) {
        return patchHasContent
          ? [{ ...(patch as object), isDefault: true } as T]
          : list;
      }
      const idx = list.findIndex((r) => r.isDefault) !== -1
        ? list.findIndex((r) => r.isDefault)
        : 0;
      const next = [...list];
      next[idx] = { ...next[idx], ...patch, isDefault: true } as T;
      return next;
    };

    const updatedAddresses = mergeIntoDefault(
      contactData.addresses ?? [],
      {
        street: contactData.strasse,
        postalCode: contactData.plz,
        city: contactData.ort,
        ortsteil: contactData.ortsteil,
        type: contactData.defaultAddress?.type || "private",
      } as any,
    );

    const updatedBankAccounts = mergeIntoDefault(
      contactData.bankAccounts ?? [],
      {
        iban: contactData.iban,
        bic: contactData.bic,
        bankName: contactData.kreditinstitut,
        accountHolder: contactData.accountHolder,
        directDebit: contactData.directDebit,
      } as any,
    );

    // FE L: form→API mapping lives in `formToApi` now. We merge in the
    // freshly-computed default-row arrays for addresses/bankAccounts on
    // top of the German→English renamed payload.
    const payload = {
      ...formToApi(contactData),
      addresses: updatedAddresses,
      bankAccounts: updatedBankAccounts,
    };

    // FE I: route handleSave through PUT /api/contact/:id (full-document
    // update path) instead of POST /api/contact (the create path, which
    // re-runs the FO/BO defaults logic and silently strips ownership —
    // audit Bug A). PUT skips that logic and uses repo.preload, so unset
    // fields don't get clobbered.
    const kontaktId =
      id ?? initialValues.kontaktId ?? contactData.kontaktId;
    if (!kontaktId) {
      dispatchToast(
        <Toast>
          <ToastTitle>Speichern nicht möglich</ToastTitle>
          <ToastBody>Kontakt-ID fehlt — bitte Seite neu laden.</ToastBody>
        </Toast>,
        { intent: "error" },
      );
      return;
    }
    const res = await fetch(apiUrl(`contact/${kontaktId}`), {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    if (res.ok) {
      await res.json();
      dispatchToast(
        <Toast>
          <ToastTitle>Gespeichert</ToastTitle>
          <ToastBody>Der Kontakt wurde erfolgreich gespeichert.</ToastBody>
        </Toast>,
        { intent: "success" }
      );
    } else {
      dispatchToast(
        <Toast>
          <ToastTitle>Fehler</ToastTitle>
          <ToastBody>Speichern fehlgeschlagen.</ToastBody>
        </Toast>,
        { intent: "error" }
      );
    }
  };

  const handleDelete = async () => {
    if (!contactData) return;

    const res = await fetch(apiUrl(`contact/${id}`), {
      method: "DELETE",
    });

    if (res.ok) {
      // Weiterleitung oder Aktualisierung der Ansicht nach dem Löschen
    }
  };

  /**
   * Single-field patch via BE 6's `PATCH /api/contact/:id`. Replaces the
   * pre-FE-I "POST the entire contact" anti-pattern, which routed through
   * the create endpoint and silently reset boType / boUserId / boTeamId /
   * accessLevel (audit Bug A) on every dropdown click. PATCH only touches
   * the fields in the body.
   *
   * Each handler:
   *   - optimistically updates local state for instant UI feedback
   *   - awaits the PATCH so failures surface
   *   - on failure: rolls back local state and toasts an error
   */
  const patchContactField = async (
    patch: Partial<ContactDetailsFormValues>,
  ): Promise<boolean> => {
    const kontaktId = id ?? initialValues.kontaktId ?? contactData?.kontaktId;
    if (!kontaktId) return false;
    try {
      const res = await fetch(apiUrl(`contact/${kontaktId}`), {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(patch),
      });
      if (!res.ok) throw new Error(`PATCH ${res.status}`);
      return true;
    } catch (err) {
      console.error("Failed to patch contact field", patch, err);
      dispatchToast(
        <Toast>
          <ToastTitle>Speichern fehlgeschlagen</ToastTitle>
          <ToastBody>Die Änderung konnte nicht übernommen werden.</ToastBody>
        </Toast>,
        { intent: "error" },
      );
      return false;
    }
  };

  // --- Kommunikation: privat / geschäftlich helpers ---
  // Communications are distinguished by (type, label). We use the canonical
  // lower-case labels 'privat' / 'geschäftlich'. `commValue` falls back to the
  // legacy unlabeled default row for 'privat' so existing data still shows up.
  const commValue = (type: string, label: "privat" | "geschäftlich"): string => {
    const comms = contactData?.communications ?? [];
    const labeled = comms.find(
      (c: any) => c.type === type && (c.label ?? "").toLowerCase() === label,
    );
    if (labeled?.value != null) return labeled.value;
    if (label === "privat") {
      const legacy = comms.find((c: any) => c.type === type && c.isDefault);
      if (legacy?.value != null) return legacy.value;
    }
    return "";
  };

  const setComm = (
    type: string,
    label: "privat" | "geschäftlich",
    value: string,
  ) => {
    setContactData((cd) => {
      if (!cd) return cd;
      const comms = [...(cd.communications || [])];
      let idx = comms.findIndex(
        (c: any) => c.type === type && (c.label ?? "").toLowerCase() === label,
      );
      // First edit of a privat field may target a legacy unlabeled default row.
      if (idx === -1 && label === "privat") {
        idx = comms.findIndex(
          (c: any) => c.type === type && c.isDefault && !c.label,
        );
      }
      if (idx !== -1) {
        comms[idx] = { ...comms[idx], value, label, isDefault: label === "privat" };
      } else {
        comms.push({ type, value, label, isDefault: label === "privat" });
      }
      return { ...cd, communications: comms };
    });
  };

  // Kontaktverantwortlicher: FO = single user (foUserId), BO = AppTeam (boTeamId,
  // boType='TEAM'). Both are persisted through the RBAC ownership endpoint
  // (assignOwnership) — they control record visibility, not just labels.
  const renderOwnershipFields = () => {
    const foValue = contactData?.foUserId ?? "";
    const boValue =
      contactData?.boType === "TEAM" ? (contactData?.boTeamId ?? "") : "";
    // Valid FO = users with an FO part in their department ('FO' or 'FO+BO'), not
    // admins. Keep the currently-assigned user visible even if their dept/role differs.
    const foUsers = users.filter(
      (u) => ((u.department ?? "").split("+").includes("FO") && u.role !== "admin") || u.id === foValue,
    );
    return (
      <>
        {/* Kontaktverantwortlicher FO (Benutzer) */}
        <div className={styles.fieldRow}>
          <span className={styles.label}>Kontaktverantwortlicher FO</span>
          <div className={styles.inputContainer}>
            <Select
              value={foValue}
              onChange={(e) => void assignOwnership({ foUserId: e.target.value || null })}
              style={{ width: "100%" }}
            >
              <option value="">— wählen —</option>
              {foUsers.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name}
                </option>
              ))}
            </Select>
          </div>
        </div>

        {/* Kontaktverantwortlicher BO (Team) */}
        <div className={styles.fieldRow}>
          <span className={styles.label}>Kontaktverantwortlicher BO</span>
          <div className={styles.inputContainer}>
            <Select
              value={boValue}
              onChange={(e) => {
                const teamId = e.target.value || null;
                void assignOwnership({
                  boType: teamId ? "TEAM" : "NONE",
                  boTeamId: teamId,
                });
              }}
              style={{ width: "100%" }}
            >
              <option value="">— wählen —</option>
              {appTeams.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </Select>
          </div>
        </div>
      </>
    );
  };

  // Renders one privat/geschäftlich communication input row (phone/mobile get a
  // Call button, email gets a Mail button). Keeps the six split rows DRY.
  const renderCommRow = (
    label: string,
    type: "phone" | "mobile" | "email",
    variant: "privat" | "geschäftlich",
  ) => {
    const value = commValue(type, variant);
    const action =
      type === "email" ? (
        <Button icon={<MailEdit24Regular />} onClick={() => handleEmail(value)} />
      ) : (
        <Button icon={<Call24Regular />} onClick={() => handleCall(value)} />
      );
    return (
      <div className={styles.fieldRow}>
        <span className={styles.label}>{label}</span>
        <div className={styles.inputContainer} style={{ display: "flex", gap: "10px", alignItems: "center" }}>
          {type === "email" ? (
            <Input
              value={value}
              placeholder={label}
              onChange={(_, d) => setComm(type, variant, d.value)}
              style={{ flex: 1 }}
            />
          ) : (
            <FluentTelefonInput
              value={value}
              placeholder={label}
              onChange={(v) => setComm(type, variant, v)}
              geschaeftlich={variant === "geschäftlich"}
              style={{ flex: 1 }}
            />
          )}
          {action}
        </div>
      </div>
    );
  };

  /**
   * Assign the RBAC ownership (Kontaktverantwortlicher) of the contact via the
   * dedicated `PUT /contact/:id/ownership` endpoint. FO is a single user
   * (foUserId, required); BO can be NONE or a team. This is the source of truth
   * for record-level visibility — it cascades to linked entities server-side.
   * Editing is gated to admin / gfgl / current FO (403 otherwise).
   */
  const assignOwnership = async (next: {
    foUserId?: string | null;
    boType?: "NONE" | "TEAM";
    boTeamId?: string | null;
  }): Promise<void> => {
    const kontaktId = id ?? initialValues.kontaktId ?? contactData?.kontaktId;
    if (!kontaktId) return;

    const foUserId = (next.foUserId ?? contactData?.foUserId ?? "").trim();
    const boType = next.boType ?? (contactData?.boType as "NONE" | "TEAM") ?? "NONE";
    const boTeamId = next.boTeamId ?? contactData?.boTeamId ?? null;

    // The endpoint requires an FO. Block BO-only assignment with a clear hint.
    if (!foUserId) {
      dispatchToast(
        <Toast>
          <ToastTitle>Bitte zuerst FO setzen</ToastTitle>
          <ToastBody>Der Kontaktverantwortliche (FO) muss gesetzt sein, bevor ein BO-Team zugeordnet werden kann.</ToastBody>
        </Toast>,
        { intent: "warning" },
      );
      return;
    }

    const bo =
      boType === "TEAM" && boTeamId
        ? { type: "TEAM" as const, id: boTeamId }
        : { type: "NONE" as const, id: null };

    // Optimistic update + snapshot for rollback.
    const prev = {
      foUserId: contactData?.foUserId,
      boType: contactData?.boType,
      boTeamId: contactData?.boTeamId,
    };
    setContactData((cd) =>
      cd
        ? {
            ...cd,
            foUserId,
            boType: bo.type,
            boTeamId: bo.type === "TEAM" ? bo.id : null,
          }
        : cd,
    );

    try {
      const res = await fetch(apiUrl(`contact/${kontaktId}/ownership`), {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ foUserId, bo }),
      });
      if (res.status === 403) {
        setContactData((cd) => (cd ? { ...cd, ...prev } : cd));
        dispatchToast(
          <Toast>
            <ToastTitle>Keine Berechtigung</ToastTitle>
            <ToastBody>Nur Eigentümer (FO), GF oder Admin dürfen die Zuordnung ändern.</ToastBody>
          </Toast>,
          { intent: "error" },
        );
        return;
      }
      if (!res.ok) throw new Error(`PUT ownership ${res.status}`);
      dispatchToast(
        <Toast>
          <ToastTitle>Zuordnung gespeichert</ToastTitle>
          <ToastBody>Der Kontaktverantwortliche wurde aktualisiert.</ToastBody>
        </Toast>,
        { intent: "success" },
      );
    } catch (err) {
      console.error("Failed to assign contact ownership", err);
      setContactData((cd) => (cd ? { ...cd, ...prev } : cd));
      dispatchToast(
        <Toast>
          <ToastTitle>Speichern fehlgeschlagen</ToastTitle>
          <ToastBody>Die Zuordnung konnte nicht übernommen werden.</ToastBody>
        </Toast>,
        { intent: "error" },
      );
    }
  };

  const handlePhaseChange = async (phase: string) => {
    const prev = contactData?.phase;
    setContactData((cd) => (cd ? { ...cd, phase } : cd));
    const ok = await patchContactField({ phase });
    if (!ok) setContactData((cd) => (cd ? { ...cd, phase: prev } : cd));
  };

  const handleRelevanzChange = async (relevanz: number) => {
    const prev = contactData?.relevanz;
    setContactData((cd) =>
      cd ? { ...cd, contactRelevance: relevanz, relevanz } : cd,
    );
    const ok = await patchContactField({
      contactRelevance: relevanz,
      relevanz,
    } as Partial<ContactDetailsFormValues>);
    if (!ok) {
      setContactData((cd) =>
        cd ? { ...cd, contactRelevance: prev, relevanz: prev } : cd,
      );
    }
  };

  const handleJahresgespraechChange = async (date: string) => {
    const prev = contactData?.letztesJahresgespraech;
    setContactData((cd) => (cd ? { ...cd, letztesJahresgespraech: date } : cd));
    const ok = await patchContactField({ letztesJahresgespraech: date });
    if (!ok) {
      setContactData((cd) =>
        cd ? { ...cd, letztesJahresgespraech: prev } : cd,
      );
    }
  };

  const handleEmpfehlungSearch = async () => {
    if (!empfehlungSearch) return;
    setIsEmpfehlungLoading(true);
    try {
      const res = await fetch(apiUrl("contact", { search: empfehlungSearch, typeFilter: "Privat", limit: 50 }));
      const data = await res.json();
      setEmpfehlungResults(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Failed to search recommendation source", err);
    } finally {
      setIsEmpfehlungLoading(false);
    }
  };

  const selectEmpfehlung = async (item: EmpfehlungResult) => {
    const empfehlungsgeber =
      item.displayName || `${item.lastname ?? ''}, ${item.firstName ?? ''}`;
    const prev = contactData?.empfehlungsgeber;
    setContactData((cd) => (cd ? { ...cd, empfehlungsgeber } : cd));
    setIsEmpfehlungModalOpen(false);
    const ok = await patchContactField({ empfehlungsgeber });
    if (!ok) {
      setContactData((cd) => (cd ? { ...cd, empfehlungsgeber: prev } : cd));
    }
  };

  const handleGenerateFormLink = async (): Promise<boolean> => {
    const kontaktId = getKontaktIdForPublicForm();
    if (!kontaktId) {
      setLinkError("Kein Kontakt vorhanden, für den ein Link erzeugt werden kann.");
      return false;
    }

    setIsGeneratingLink(true);
    setLinkError(null);
    try {
      const res = await fetch(apiUrl(`contact/${kontaktId}/public-form-link`), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ formType: selectedPublicFormType }),
      });
      if (!res.ok) {
        if (res.status === 409) {
          let message = "Es existiert bereits ein aktives Formular dieses Typs.";
          try {
            const data = await res.json();
            if (typeof data?.message === "string") message = data.message;
          } catch {}
          setLinkError(message);
          await loadActivePublicForms();
          return false;
        }
        throw new Error(`Fehler beim Erzeugen des Links: ${res.status}`);
      }
      await loadActivePublicForms();
      return true;
    } catch (e: any) {
      setLinkError(e?.message ?? "Unbekannter Fehler beim Erzeugen des Links.");
      return false;
    } finally {
      setIsGeneratingLink(false);
    }
  };

  const handleOpenPublicFormDialog = () => {
    setLinkError(null);
    setIsPublicFormDialogOpen(true);
  };

  const handleConfirmPublicFormDialog = async () => {
    const created = await handleGenerateFormLink();
    if (created) {
      setIsPublicFormDialogOpen(false);
    }
  };

  const handleApplyPublicForm = async (form: ActivePublicForm) => {
    const kontaktId = getKontaktIdForPublicForm();
    if (!kontaktId) {
      setLinkError("Kein Kontakt vorhanden, für den Werte übernommen werden können.");
      return;
    }

    setApplyingFormToken(form.token);
    setLinkError(null);
    try {
      const res = await fetch(apiUrl(`contact/${kontaktId}/public-form-apply`), {
        method: "POST",
      });

      if (!res.ok) {
        throw new Error(`Fehler beim Übernehmen der Werte: ${res.status}`);
      }

      dispatchToast(
        <Toast>
          <ToastTitle>Werte übernommen</ToastTitle>
          <ToastBody>Die Daten aus dem Kundenformular wurden übernommen.</ToastBody>
        </Toast>,
        { intent: "success" }
      );

      // FE J: same `?include=relations` shape as the mount-time fetch.
      const contactRes = await fetch(
        apiUrl(`contact/${kontaktId}`, { include: 'relations' }),
      );
      if (contactRes.ok) {
        const latest = await contactRes.json();
        setContactData((prev: any) => ({ ...prev, ...latest }));
      }

      await loadActivePublicForms();
    } catch (e: any) {
      setLinkError(e?.message ?? "Unbekannter Fehler beim Übernehmen der Werte.");
    } finally {
      setApplyingFormToken(null);
    }
  };

  const handleRevokePublicForm = async (form: ActivePublicForm) => {
    const kontaktId = getKontaktIdForPublicForm();
    if (!kontaktId) return;

    setRevokingFormToken(form.token);
    setLinkError(null);
    try {
      const res = await fetch(
        apiUrl(`contact/${kontaktId}/public-form-link/${encodeURIComponent(form.token)}`),
        { method: "DELETE" }
      );
      if (!res.ok) {
        throw new Error(`Fehler beim Widerrufen des Formulars: ${res.status}`);
      }
      await loadActivePublicForms();
    } catch (e: any) {
      setLinkError(e?.message ?? "Unbekannter Fehler beim Widerrufen des Formulars.");
    } finally {
      setRevokingFormToken(null);
    }
  };

  const handleCopyLink = async (url: string) => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      // ignore clipboard errors
    }
  };

  const onTabSelect = (_event: SelectTabEvent, data: SelectTabData) => {
    setSelectedTab(data.value as string);
  };
  return (
    <div className={styles.root}>
      {/*
        Email 03.12.2025 BH: Inkasso => Zahlunsgverkehr
        Meeting 01.12.2025: BH, DC Allgemein | Organisation | Kennzahlen | Risiken | Verträge | Schäden | Vorgänge | Termine/Wiedervorlage | Schriftverkehr | Inkasso | Kundentagebuch | Notizen     
      */}
      <div className={styles.stickyBar}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "16px" }}>



          <FormHeader
            title={contactData?.displayName ?? contactData?.lastname ?? ""}
            icon={
              <div style={{ position: 'relative', cursor: 'pointer' }} onClick={() => elementMitId('contact-photo-upload-header')?.click()}>
                <Avatar
                  size={64}
                  image={contactData?.photo_url ? { src: apiUrl(contactData.photo_url) } : undefined}
                  name={contactData?.photo_url ? undefined : (contactData?.lastname || "C")}
                />
                <div style={{
                  position: 'absolute',
                  bottom: -4,
                  right: -4,
                  background: tokens.colorNeutralBackground1,
                  borderRadius: '50%',
                  padding: '4px',
                  boxShadow: tokens.shadow4,
                  display: 'flex',
                }}>
                  {uploadingPhoto ? <Spinner size="tiny" /> : <CameraAdd24Regular />}
                </div>
                <input
                  id="contact-photo-upload-header"
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  onChange={handlePhotoUpload}
                />
              </div>
            }
            typ={contactData?.type?.type ?? contactData?.typ}
            relevanz={contactData?.contactRelevance ?? contactData?.relevanz}
            phase={contactData?.phase}
            value={contactData?.contactValue ?? contactData?.value}
            authority={contactData?.authority ?? { erstinformation: false, werbewiederspruch: false }}
            jahresgespraech={contactData?.letztesJahresgespraech}
            empfehlungsgeber={contactData?.empfehlungsgeber}
            status={contactData?.status}
            kontaktStatus={contactData?.kontaktStatus}
            onSave={handleSave} onDelete={handleDelete}
            onAdd={() => {
              if (selectedTab === "risiken") {
                const cid = Number(initialValues.kontaktId) || null;
                const cname =
                  displayContactName(contactData) ??
                  displayContactName(initialValues as never) ??
                  null;
                openRiskFormWindow(openWindow, {
                  contactId: cid,
                  contactName: cname,
                  initialSize: { width: 1080, height: 720 },
                });
              }
            }}
            onOpenNotes={() => setIsNotesModalOpen(true)}
            notesCount={0}
            onPhaseChange={handlePhaseChange}
            onRelevanzChange={handleRelevanzChange}
            onJahresgespraechChange={handleJahresgespraechChange}
            onEmpfehlungsgeberClick={() => setIsEmpfehlungModalOpen(true)}
            contactId={id}
          />
        </div>

        <div className={styles.publicFormActions} />

        {linkError && (
          <div style={{ color: tokens.colorStatusDangerForeground1, marginBottom: 8 }}>{linkError}</div>
        )}

        <Dialog open={isPublicFormDialogOpen} onOpenChange={(_, data) => setIsPublicFormDialogOpen(data.open)}>
          <DialogSurface className={styles.publicDialogSurface}>
            <DialogBody>
              <DialogTitle className={styles.publicDialogTitle}>Kundenformular auswählen</DialogTitle>
              <DialogContent className={styles.publicDialogContent}>
                <div>
                  <div className={styles.publicDialogCustomerLabel}>Kunde</div>
                  <Input
                    className={styles.publicDialogCustomerInput}
                    readOnly
                    value={contactData?.displayName ?? contactData?.lastname ?? "-"}
                  />
                </div>
                <div>
                  <div className={styles.publicDialogSelectLabel}>Kundenformular auswählen</div>
                  <Select
                    className={styles.publicDialogSelect}
                    value={selectedPublicFormType}
                    onChange={(event) => setSelectedPublicFormType(event.target.value as PublicFormType)}
                  >
                    <option value="contact">PublicCustomerForm</option>
                    <option value="address">PublicCustomerCheckAddressForm</option>
                    <option value="risikoanalyse">Risikoanalyse (Scroll)</option>
                    {/* risikoanalyse-step entfernt — ist intern (Beratermodus),
                        nicht als Kundenlink anbietbar (BE rejected). */}
                    <option value="risikoanalyse-kurz">Risikoanalyse (Kurzversion · Schritt)</option>
                    <option value="risikoanalyse-kurz-scroll">Risikoanalyse (Kurzversion · Scroll)</option>
                    <option value="hub">Formular Übersicht (Hub)</option>
                  </Select>
                </div>
                <p className={styles.publicDialogHint}>
                  Wählen Sie das gewünschte Formular aus und erstellen Sie anschließend den Kundenlink.
                </p>
              </DialogContent>
              <DialogActions>
                <Button className={styles.publicDialogSecondaryButton} size="small" appearance="secondary" onClick={() => setIsPublicFormDialogOpen(false)}>
                  Schließen
                </Button>
                <Button
                  size="small"
                  className={styles.publicDialogPrimaryButton}
                  appearance="primary"
                  onClick={handleConfirmPublicFormDialog}
                  disabled={isGeneratingLink}
                >
                  {isGeneratingLink ? "Erzeuge..." : "Kundenformular erstellen"}
                </Button>
              </DialogActions>
            </DialogBody>
          </DialogSurface>
        </Dialog>






        <div className={styles.tabsRow}>
          <TabList selectedValue={selectedTab} onTabSelect={onTabSelect} className={styles.tabsList}>
            <Tab style={{ paddingLeft: 0 }} id="allgemein" value="allgemein">
              Allgemein
            </Tab>
            {((contactData?.typ === "Firma" || contactData?.type?.type === "Firma")) && <Tab id="metrics" value="metrics">Kennzahlen</Tab>}
            <Tab id="risiken" value="risiken">Risiken</Tab>
            <Tab id="vertraege" value="vertraege">Verträge</Tab>
            <Tab id="schaeden" value="schaeden">Schäden</Tab>
            <Tab id="vorgaenge" value="vorgaenge">
              Kundentagebuch
            </Tab>
            <Tab id="events" value="events">Termine</Tab>
            <Tab id="mail" value="mail">Schriftverkehr</Tab>
            <Tab id="inkasso" value="inkasso">Zahlungsverkehr</Tab>
            <Tab id="diary" value="diary">Archiv</Tab>
            <Tab id="documents" value="documents">Dokumente</Tab>
          </TabList>

          <div style={{ position: "relative", display: "inline-flex" }}>
            <Menu>
              <MenuTrigger disableButtonEnhancement>
                <Button
                  className={styles.publicFormMenuButton}
                  appearance="subtle"
                  size="small"
                  icon={<MoreVertical20Regular />}
                  aria-label="Kundenformular Menü"
                />
              </MenuTrigger>
              <MenuPopover>
                <MenuList>
                  <MenuItem className={styles.publicMenuHeader}>Kontakt</MenuItem>
                  <MenuItem onClick={handleOpenPublicFormDialog}>Kundenformular erstellen</MenuItem>
                </MenuList>
              </MenuPopover>
            </Menu>
            {activePublicForms.length > 0 && (
              <sup
                aria-label={`${activePublicForms.length} offene Kundenformulare`}
                style={{
                  position: "absolute",
                  top: -2,
                  right: -2,
                  color: "red",
                  fontWeight: "bold",
                  fontSize: "0.75rem",
                  pointerEvents: "none",
                }}
              >
                {activePublicForms.length}
              </sup>
            )}
          </div>
        </div>
      </div>

      {/* Scrollbarer Inhalt darunter */}
      {(() => {
        const resolvedKontaktId = id ? Number(id) : Number(initialValues.kontaktId);
        return (
          <>
            {selectedTab === "metrics" && resolvedKontaktId ? (
              <KennzahlenTab
                contactId={resolvedKontaktId}
                contactName={contactData?.displayName}
              />
            ) : null}
          </>
        );
      })()}

      {(() => {
        // Risikoanalyse-Links sind bereits im Risiken-Tab in der Kundenanalyse-Box sichtbar
        // — daher hier ausblenden, damit der Hinweis nicht doppelt erscheint.
        const visibleForms = activePublicForms.filter(
          (f) =>
            f.formType !== "risikoanalyse" &&
            f.formType !== "risikoanalyse-step" &&
            f.formType !== "risikoanalyse-kurz" &&
            f.formType !== "risikoanalyse-kurz-scroll",
        );
        if (visibleForms.length === 0) return null;
        return (
        <div className={styles.publicLinkCard}>
          <h3 className={styles.publicLinkTitle}>
            Kundenformulare ({visibleForms.length})
          </h3>
          {visibleForms.map((form) => {
            const typeLabel =
              form.formType === "address"
                ? "Adresspruefung"
                : form.formType === "risikoanalyse"
                  ? "Risikoanalyse"
                  : form.formType === "risikoanalyse-step"
                    ? "Risikoanalyse (Schritt)"
                    : form.formType === "risikoanalyse-kurz"
                      ? "Risikoanalyse (Kurzversion · Schritt)"
                      : form.formType === "risikoanalyse-kurz-scroll"
                        ? "Risikoanalyse (Kurzversion · Scroll)"
                        : "Kundenformular";
            const isApplying = applyingFormToken === form.token;
            const isRevoking = revokingFormToken === form.token;
            return (
              <div key={form.token} className={styles.publicLinkRow} style={{ marginBottom: 8 }}>
                <span style={{ minWidth: 140, fontWeight: 600 }}>{typeLabel}</span>
                <Input
                  readOnly
                  value={form.url}
                  style={{ flex: 1, minWidth: 260 }}
                />
                <Button
                  size="small"
                  appearance="secondary"
                  icon={<Copy24Regular />}
                  onClick={() => handleCopyLink(form.url)}
                >
                  Kopieren
                </Button>
                <Link href={form.url} target="_blank">
                  Öffnen
                </Link>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 8, whiteSpace: "nowrap", marginLeft: "auto" }}>
                  <span
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      backgroundColor:
                        form.status === "FILLED"
                          ? tokens.colorStatusSuccessForeground1
                          : tokens.colorNeutralStroke2,
                    }}
                  />
                  <span style={{ color: tokens.colorNeutralForeground1 }}>
                    {isCheckingPublicFormStatus
                      ? "Status wird geprüft"
                      : form.submitted
                        ? "Ausgefüllt"
                        : "Nicht ausgefüllt"}
                  </span>
                </span>
                {form.formType === "contact" && (
                  <Button
                    size="small"
                    appearance="secondary"
                    onClick={() => handleApplyPublicForm(form)}
                    disabled={!form.canApply || isApplying}
                  >
                    {isApplying ? "Übernehme..." : "Alle Werte übernehmen"}
                  </Button>
                )}
                <Button
                  size="small"
                  appearance="secondary"
                  onClick={() => handleRevokePublicForm(form)}
                  disabled={isRevoking}
                >
                  {isRevoking ? "Widerrufe..." : "Widerrufen"}
                </Button>
              </div>
            );
          })}
        </div>
        );
      })()}

      {selectedTab === "allgemein" && (contactData?.typ === "Firma" || contactData?.type?.type === "Firma") && (
        <div>
          <div className={styles.grid}>

            {/* Kontaktnummern */}
            <span className={styles.labelSection}>Kontaktnummern</span>

            {/* Interne Kontaktnummer */}
            <div className={styles.fieldRow}>
              <span className={styles.label}>Interne Kontaktnummer</span>
              <div className={styles.inputContainer}>
                <Input
                  value={contactData?.contactNbrInt ?? ""}
                  placeholder="Interne Kontaktnummer"
                  onChange={(_, data) => setContactData(cd => cd ? { ...cd, contactNbrInt: data.value } : cd)}
                  style={{ width: "100%" }}
                />
              </div>
            </div>

            {/* Allfinanz Kontaktnummer (Dummy) */}
            <div className={styles.fieldRow}>
              <span className={styles.label}>Allfinanz Kontaktnummer</span>
              <div className={styles.inputContainer}>
                <Input
                  value={contactData?.contactNbrAllfinanz ?? ""}
                  placeholder="Allfinanz Kontaktnummer"
                  onChange={(_, data) => setContactData(cd => cd ? { ...cd, contactNbrAllfinanz: data.value } : cd)}
                  style={{ width: "100%" }}
                />
              </div>
            </div>

            {/* Externe Kontaktnummer */}
            <div className={styles.fieldRow}>
              <span className={styles.label}>Externe Kontaktnummer</span>
              <div className={styles.inputContainer}>
                <Input
                  value={contactData?.contactNbrExt ?? ""}
                  placeholder="Externe Kontaktnummer"
                  onChange={(_, data) => setContactData(cd => cd ? { ...cd, contactNbrExt: data.value } : cd)}
                  style={{ width: "100%" }}
                />
              </div>
            </div>
            <span className={styles.labelSection}>Allgemeine Informationen</span>
            {/* Name */}
            <div className={styles.fieldRow}>
              <span className={styles.label}>Firmenname</span>
              <div className={styles.inputContainer}>
                <Input
                  value={contactData?.lastname ?? ""}
                  placeholder="Nachname"
                  onChange={(_, data) =>
                    setContactData((cd) =>
                      cd ? { ...cd, lastname: data.value } : cd,
                    )
                  }
                  style={{ width: "100%" }}
                />
              </div>
            </div>

            {/* Gründungsdatum */}
            <div className={styles.fieldRow}>
              <span className={styles.label}>Gründungsdatum</span>
              <div className={styles.inputContainer}>
                <Input
                  type="date"
                  value={contactData?.gruendungsdatum ?? ""}
                  placeholder="YYYY-MM-DD"
                  onChange={(_, data) =>
                    setContactData((cd) =>
                      cd ? { ...cd, gruendungsdatum: data.value } : cd,
                    )
                  }
                  style={{ width: "100%" }}
                />
              </div>
            </div>

            {/* Rechtsvorgänger (Old Legal Form) */}
            <div className={styles.fieldRow}>
              <span className={styles.label}>Rechtsvorgänger</span>
              <div className={styles.inputContainer}>
                {legalFormLoading ? (
                  <Select disabled style={{ width: "100%" }}>
                    <option>Lädt…</option>
                  </Select>
                ) : (
                  <Select
                    value={oldLegalForm}
                    onChange={(e) => {
                      const nextValue = e.target.value;
                      const selected = legalFormOptions.find(
                        (lf) => lf.legalForm === nextValue,
                      );
                      setOldLegalForm(nextValue);
                      setContactData((cd) =>
                        cd
                          ? {
                            ...cd,
                            oldLegalForm: selected ? selected : null,
                          }
                          : cd,
                      );
                    }}
                    style={{ width: "100%" }}
                  >
                    <option value="">— wählen —</option>
                    {legalFormOptions.map((lf) => (
                      <option key={lf.id} value={lf.legalForm}>
                        {lf.legalForm}
                      </option>
                    ))}
                  </Select>
                )}
              </div>
            </div>

            {/* Geschäftsjahr (Bilanzstichtag) */}
            <div className={styles.fieldRow}>
              <span className={styles.label}>Geschäftsjahr (Bilanzstichtag)</span>
              <div className={styles.inputContainer}>
                <Input
                  type="text"
                  value={contactData?.financialYearEnd ?? ""}
                  placeholder="z.B. 31.12"
                  maxLength={5}
                  pattern="\d{2}\.\d{2}"
                  onChange={(_, data) =>
                    setContactData(cd => cd ? { ...cd, financialYearEnd: data.value } : cd)
                  }
                  style={{ width: "100%" }}
                />{" "}
              </div>
            </div>

            <div className={styles.fieldRow}>
              <span className={styles.label}>Vorsteuerabzugsberechtigung</span>
              <div className={styles.inputContainer}>
                <Dropdown
                  value={
                    contactData?.vorsteuerAbzugsberechtigt === undefined
                      ? ""
                      : contactData.vorsteuerAbzugsberechtigt
                        ? "Ja"
                        : "Nein"
                  }
                  placeholder="Vorsteuerabzugsberechtigung wählen"
                  onOptionSelect={(_, data) =>
                    setContactData(cd => cd ? { ...cd, vorsteuerAbzugsberechtigt: data.optionValue === "Ja" } : cd)
                  }
                  style={{ width: "100%" }}
                >
                  <Option value="Ja">Ja</Option>
                  <Option value="Nein">Nein</Option>
                </Dropdown>
              </div>
            </div>

            {/* Rechtsform (UI-Label; Daten bleiben in gesellschaftsform/legalForm). */}
            <div className={styles.fieldRow}>
              <span className={styles.label}>Rechtsform</span>
              <div className={styles.inputContainer}>
                {legalFormLoading ? (
                  <Select disabled style={{ width: "100%" }}>
                    <option>Lädt…</option>
                  </Select>
                ) : (
                  <Select
                    value={gesellschaftsform}
                    onChange={(e) => {
                      const newValue = e.target.value;
                      const selected = legalFormOptions.find(
                        (lf) => lf.legalForm === newValue,
                      );

                      // Auto-update Old Legal Form (Rechtsvorgänger) with PREVIOUS value
                      const previousLegalForm = gesellschaftsform;
                      const previousSelected = legalFormOptions.find(lf => lf.legalForm === previousLegalForm);

                      setGesellschaftsform(newValue);

                      // Update State Logic
                      setContactData((cd) => {
                        if (!cd) return cd;

                        const newData: any = {
                          ...cd,
                          legalForm: selected ? selected : null,
                        };

                        // Logic: If I change current legal form, set old one to previous current
                        if (previousSelected && previousLegalForm && previousLegalForm !== newValue) {
                          setOldLegalForm(previousLegalForm);
                          newData.oldLegalForm = previousSelected;
                        }

                        return newData;
                      });
                    }}
                    style={{ width: "100%" }}
                  >
                    <option value="">
                      {initialValues.gesellschaftsform ?? "— wählen —"}
                    </option>
                    {legalFormOptions.map((lf) => (
                      <option key={lf.id} value={lf.legalForm}>
                        {lf.legalForm}
                      </option>
                    ))}
                  </Select>
                )}
              </div>
            </div>

            {/* Branche */}
            <div className={styles.fieldRow}>
              <span className={styles.label}>Branche</span>
              <div className={styles.inputContainer}>
                <Input
                  value={contactData?.branche ?? ""}
                  placeholder="Branche"
                  onChange={(_, data) => setContactData(cd => cd ? { ...cd, branche: data.value } : cd)}
                  style={{ width: "100%" }}
                />
              </div>
            </div>

            {/* Tätigkeit */}
            <div className={styles.fieldRow}>
              <span className={styles.label}>Tätigkeit</span>
              <div className={styles.inputContainer}>
                <Input
                  value={contactData?.taetigkeit ?? ""}
                  placeholder="Tätigkeit"
                  onChange={(_, data) => setContactData(cd => cd ? { ...cd, taetigkeit: data.value } : cd)}
                  style={{ width: "100%" }}
                />
              </div>
            </div>

            {/* Kontaktverantwortlicher FO (Benutzer) + BO (Team) */}
            {renderOwnershipFields()}

            {/* Mitgliedschaften in Verbänden */}
            <div className={styles.fieldRow}>
              <span className={styles.label}>Mitgliedschaften</span>
              <div className={styles.inputContainer}>
                <Input
                  value={contactData?.mitgliedschaften ?? ""}
                  placeholder="Mitgliedschaften"
                  onChange={(_, data) => setContactData(cd => cd ? { ...cd, mitgliedschaften: data.value } : cd)}
                  style={{ width: "100%" }}
                />
              </div>
            </div>

            {/* Steuer- & Rechtsdaten — nur für Firma-Kontakte. Boolean-Felder
                folgen dem gleichen Ja/Nein-Dropdown-Pattern wie
                Vorsteuerabzugsberechtigung weiter oben. */}
            <span className={styles.labelSection}>Steuer- & Rechtsdaten</span>

            <div className={styles.fieldRow}>
              <span className={styles.label}>Steuernummer</span>
              <div className={styles.inputContainer}>
                <Input
                  value={contactData?.steuernummer ?? ""}
                  placeholder="z. B. 12/345/67890"
                  onChange={(_, data) => setContactData(cd => cd ? { ...cd, steuernummer: data.value } : cd)}
                  style={{ width: "100%" }}
                />
              </div>
            </div>

            <div className={styles.fieldRow}>
              <span className={styles.label}>Umsatzsteuer-IdNr.</span>
              <div className={styles.inputContainer}>
                <Input
                  value={contactData?.umsatzsteuernummer ?? ""}
                  placeholder="z. B. DE123456789"
                  onChange={(_, data) => setContactData(cd => cd ? { ...cd, umsatzsteuernummer: data.value } : cd)}
                  style={{ width: "100%" }}
                />
              </div>
            </div>

            <div className={styles.fieldRow}>
              <span className={styles.label}>Handelsregister</span>
              <div className={styles.inputContainer}>
                <Dropdown
                  value={contactData?.register ?? ""}
                  placeholder="Auswählen"
                  onOptionSelect={(_, data) =>
                    setContactData(cd => cd ? { ...cd, register: (data.optionValue ?? "") as string } : cd)
                  }
                  style={{ width: "100%" }}
                >
                  <Option value="">— wählen —</Option>
                  <Option value="HRA">HRA</Option>
                  <Option value="HRB">HRB</Option>
                  <Option value="GnR">GnR</Option>
                  <Option value="PR">PR</Option>
                  <Option value="VR">VR</Option>
                </Dropdown>
              </div>
            </div>

            <div className={styles.fieldRow}>
              <span className={styles.label}>Handelsregisternummer</span>
              <div className={styles.inputContainer}>
                <Input
                  value={contactData?.handelsregisterNummer ?? ""}
                  placeholder="z. B. 12345"
                  onChange={(_, data) => setContactData(cd => cd ? { ...cd, handelsregisterNummer: data.value } : cd)}
                  style={{ width: "100%" }}
                />
              </div>
            </div>

            <div className={styles.fieldRow}>
              <span className={styles.label}>Zuständiges Finanzamt</span>
              <div className={styles.inputContainer}>
                <Input
                  value={contactData?.finanzamt ?? ""}
                  placeholder="z. B. Finanzamt Köln-Nord"
                  onChange={(_, data) => setContactData(cd => cd ? { ...cd, finanzamt: data.value } : cd)}
                  style={{ width: "100%" }}
                />
              </div>
            </div>


            {/* Anschrift */}
            <span
              className={styles.labelSection}
              style={{ display: "flex", alignItems: "center", gap: 8 }}
            >
              Anschrift{" "}
              <LayerDiagonal24Regular
                style={{ color: "purple", cursor: "pointer" }}
                onClick={() => { setDataModalTab('addresses'); setIsDataModalOpen(true); }}
              />
            </span>

            {/* Straße */}
            <div className={styles.fieldRow}>
              <span className={styles.label}>Straße</span>
              <div className={styles.inputContainer}>
                <Input
                  value={contactData?.strasse ?? ""}
                  placeholder="Straße"
                  onChange={(_, data) => setContactData(cd => cd ? { ...cd, strasse: data.value } : cd)}
                  style={{ width: "100%" }}
                />
              </div>
            </div>

            {/* PLZ */}
            <div className={styles.fieldRow}>
              <span className={styles.label}>PLZ</span>
              <div className={styles.inputContainer}>
                <Input
                  value={contactData?.plz ?? ""}
                  placeholder="PLZ"
                  onChange={(_, data) => setContactData(cd => cd ? { ...cd, plz: data.value } : cd)}
                  style={{ width: "100%" }}
                />
              </div>
            </div>

            {/* Ort */}
            <div className={styles.fieldRow}>
              <span className={styles.label}>Ort</span>
              <div className={styles.inputContainer}>
                <Input
                  value={contactData?.ort ?? ""}
                  placeholder="Ort"
                  onChange={(_, data) => setContactData(cd => cd ? { ...cd, ort: data.value } : cd)}
                  style={{ width: "100%" }}
                />
              </div>
            </div>

            {/* Ortsteil */}
            <div className={styles.fieldRow}>
              <span className={styles.label}>Ortsteil</span>
              <div className={styles.inputContainer}>
                <Input
                  value={contactData?.ortsteil ?? ""}
                  placeholder="Ortsteil"
                  onChange={(_, data) => setContactData(cd => cd ? { ...cd, ortsteil: data.value } : cd)}
                  style={{ width: "100%" }}
                />
              </div>
            </div>


            {/* Kommunikation */}
            <span
              className={styles.labelSection}
              style={{ display: "flex", alignItems: "center", gap: 8 }}
            >
              Kommunikation{" "}
              <AvailabilityPopover
                contactId={Number(initialValues.kontaktId) || (contactData?.kontaktId ? Number(contactData.kontaktId) : 0)}
                initialValue={contactData?.erreichbarkeit}
                onUpdate={(val) => setContactData(cd => cd ? { ...cd, erreichbarkeit: val } : cd)}
              />
              <LayerDiagonal24Regular
                style={{ color: "purple", cursor: "pointer" }}
                onClick={() => { setDataModalTab('communications'); setIsDataModalOpen(true); }}
              />
            </span>

            <div style={{ display: "flex", gap: "10px", alignItems: "center", marginBottom: "15px" }}>
              {(['linkedin', 'instagram', 'facebook', 'tiktok', 'xing', 'youtube', 'homepage'] as const).map((platform) => {
                const existing = (contactData?.communications || []).find((c: any) => c.type === platform);
                return (
                  <SocialMediaIcon
                    key={platform}
                    platform={platform}
                    existing={existing}
                    contactId={Number(initialValues.kontaktId) || (contactData?.kontaktId ? Number(contactData.kontaktId) : 0)}
                    onSave={(newComm: any) => {
                      setContactData((prev: any) => {
                        if (!prev) return prev;
                        const comms = prev.communications || [];
                        const exists = comms.find((c: any) => c.id === newComm.id);
                        let newComms;
                        if (exists) {
                          newComms = comms.map((c: any) => c.id === newComm.id ? newComm : c);
                        } else {
                          newComms = [...comms, newComm];
                        }
                        return { ...prev, communications: newComms };
                      });
                    }}
                  />
                );
              })}
            </div>
            <div className={styles.fieldRow}></div>

            {/* Telefon privat / geschäftlich */}
            {renderCommRow("Telefon privat", "phone", "privat")}
            {renderCommRow("Telefon geschäftlich", "phone", "geschäftlich")}

            {/* Mobil privat / geschäftlich */}
            {renderCommRow("Mobil privat", "mobile", "privat")}
            {renderCommRow("Mobil geschäftlich", "mobile", "geschäftlich")}

            {/* Fax */}
            <div className={styles.fieldRow}>
              <span className={styles.label}>Telefax</span>
              <div className={styles.inputContainer}>
                <FluentTelefonInput
                  value={contactData?.communications?.find((c: any) => c.type === 'fax' && c.isDefault)?.value ?? ""}
                  placeholder="Telefax"
                  onChange={(faxWert) => setContactData(cd => {
                    if (!cd) return cd;
                    const comms = [...(cd.communications || [])];
                    const idx = comms.findIndex(c => c.type === 'fax' && c.isDefault);
                    if (idx !== -1) {
                      comms[idx] = { ...comms[idx], value: faxWert };
                    } else {
                      comms.push({ type: 'fax', value: faxWert, isDefault: true });
                    }
                    return { ...cd, communications: comms };
                  })}
                  style={{ width: "100%" }}
                />
              </div>
            </div>

            {/* Email privat / geschäftlich */}
            {renderCommRow("Email privat", "email", "privat")}
            {renderCommRow("Email geschäftlich", "email", "geschäftlich")}

            {/* Bank */}
            <span
              className={styles.labelSection}
              style={{ display: "flex", alignItems: "center", gap: 8 }}
            >
              Bank{" "}
              <LayerDiagonal24Regular
                style={{ color: "purple", cursor: "pointer" }}
                onClick={() => { setDataModalTab('banks'); setIsDataModalOpen(true); }}
              />
            </span>

            {/* Bank */}
            <div className={styles.fieldRow}>
              <span className={styles.label}>Bank</span>
              <div className={styles.inputContainer}>
                <Input
                  value={contactData?.defaultBankAccount?.bankName ?? contactData?.bank ?? ""}
                  placeholder="Bank"
                  onChange={(_, data) => setContactData(cd => cd ? { ...cd, bank: data.value } : cd)}
                  style={{ width: "100%" }}
                />
              </div>
            </div>

            {/* IBAN */}
            <div className={styles.fieldRow}>
              <span className={styles.label}>IBAN</span>
              <div className={styles.inputContainer}>
                <Input
                  value={contactData?.defaultBankAccount?.iban ?? contactData?.iban ?? ""}
                  placeholder="IBAN"
                  onChange={(_, data) => setContactData(cd => cd ? { ...cd, iban: data.value } : cd)}
                  style={{ width: "100%" }}
                />
              </div>
            </div>

            {/* BIC */}
            <div className={styles.fieldRow}>
              <span className={styles.label}>BIC</span>
              <div className={styles.inputContainer}>
                <Input
                  value={contactData?.defaultBankAccount?.bic ?? contactData?.bic ?? ""}
                  placeholder="BIC"
                  onChange={(_, data) => setContactData(cd => {
                    if (!cd) return cd;
                    return { ...cd, bic: data.value, defaultBankAccount: { ...(cd.defaultBankAccount || {}), bic: data.value } };
                  })}
                  style={{ width: "100%" }}
                />
              </div>
            </div>

            <div className={styles.fieldRow}>
              <span className={styles.label}>Zweck</span>
              <div className={styles.inputContainer}>
                <Input
                  value={contactData?.defaultBankAccount?.bankDescription ?? ""}
                  placeholder="Zweck"
                  onChange={(_, data) => setContactData(cd => {
                    if (!cd) return cd;
                    const db = cd.defaultBankAccount || {};
                    return { ...cd, defaultBankAccount: { ...db, bankDescription: data.value } };
                  })}
                  style={{ width: "100%" }}
                />
              </div>
            </div>

            {/* Kontoinhaber */}
            <div className={styles.fieldRow}>
              <span className={styles.label}>Kontoinhaber</span>
              <div className={styles.inputContainer}>
                <Input
                  value={contactData?.accountHolder ?? ""}
                  placeholder="Kontoinhaber"
                  onChange={(_, data) => setContactData(cd => cd ? { ...cd, accountHolder: data.value } : cd)}
                  style={{ width: "100%" }}
                />
              </div>
            </div>

            {/* Bankeinzug */}
            <div className={styles.fieldRow}>
              <span className={styles.label}>Bankeinzug</span>
              <div className={styles.inputContainer}>
                <Select
                  value={contactData?.directDebit ? "ja" : "nein"}
                  onChange={(_, data) => setContactData(cd => cd ? { ...cd, directDebit: data.value === "ja" } : cd)}
                  style={{ width: "100%" }}
                >
                  <option value="nein">Nein</option>
                  <option value="ja">Ja</option>
                </Select>
              </div>
            </div>
            {/* Data Management Modal */}
            <DataManagementModal
              open={isDataModalOpen}
              onClose={setIsDataModalOpen}
              contactId={id ? Number(id) : Number(initialValues.kontaktId)}
              contactName={contactData?.displayName || "Unbekannt"}
              addresses={contactData?.addresses ?? []}
              banks={contactData?.bankAccounts ?? []}
              communications={contactData?.communications ?? []}
              relations={contactData?.relationsSrc ?? []}
              erreichbarkeit={contactData?.erreichbarkeit}
              initialTab={dataModalTab}
              onUpdateErreichbarkeit={(val) => setContactData(prev => prev ? ({ ...prev, erreichbarkeit: val }) : prev)}
            />
          </div>
          <RelationshipDetails
            kontaktId={contactData?.kontaktId ?? Number(id)}
            relations={contactData?.relationsSrc ?? []}
            contactType={(contactData?.typ as string) ?? contactData?.type?.type}
            group={contactData?.group}
            onChanged={() => setRelReload((k) => k + 1)}
          />
        </div>
      )}
      {selectedTab === "allgemein" && (contactData?.typ === "Person" || contactData?.type?.type === "Person") && (
        <div>
          <div className={styles.grid}>


            {/* Kontaktnummern */}
            <span className={styles.labelSection}>Kontaktnummern</span>

            {/* Interne Kontaktnummer */}
            <div className={styles.fieldRow}>
              <span className={styles.label}>Interne Kontaktnummer</span>
              <div className={styles.inputContainer}>
                <Input
                  value={contactData?.contactNbrInt ?? ""}
                  placeholder="Interne Kontaktnummer"
                  onChange={(_, data) => setContactData(cd => cd ? { ...cd, contactNbrInt: data.value } : cd)}
                  style={{ width: "100%" }}
                />
              </div>
            </div>

            {/* Allfinanz Kontaktnummer (Dummy) */}
            <div className={styles.fieldRow}>
              <span className={styles.label}>Allfinanz Kontaktnummer</span>
              <div className={styles.inputContainer}>
                <Input
                  value={contactData?.contactNbrAllfinanz ?? ""}
                  placeholder="Allfinanz Kontaktnummer"
                  onChange={(_, data) => setContactData(cd => cd ? { ...cd, contactNbrAllfinanz: data.value } : cd)}
                  style={{ width: "100%" }}
                />
              </div>
            </div>

            {/* Externe Kontaktnummer */}
            <div className={styles.fieldRow}>
              <span className={styles.label}>Externe Kontaktnummer</span>
              <div className={styles.inputContainer}>
                <Input
                  value={contactData?.contactNbrExt ?? ""}
                  placeholder="Externe Kontaktnummer"
                  onChange={(_, data) => setContactData(cd => cd ? { ...cd, contactNbrExt: data.value } : cd)}
                  style={{ width: "100%" }}
                />
              </div>
            </div>
            <span className={styles.labelSection}>Allgemeine Informationen</span>
            {/* Anrede */}
            <div className={styles.fieldRow}>
              <span className={styles.label}>Anrede</span>
              <div className={styles.inputContainer}>
                <Dropdown
                  aria-labelledby={dropdownIdAnrede}
                  placeholder="Anrede wählen"
                  value={
                    anredeOptions.find((o) => String(o.id) === contactData?.anrede)?.salutation ??
                    ""
                  }
                  selectedOptions={contactData?.anrede ? [contactData.anrede] : []}
                  onOptionSelect={(_e, data) =>
                    setContactData((cd) =>
                      cd ? { ...cd, anrede: data.optionValue ?? "" } : cd
                    )
                  }
                  style={{ width: "100%" }}
                >
                  {anredeOptions.map((option) => (
                    <Option key={option.id} value={String(option.id)}>
                      {option.salutation ?? ''}
                    </Option>
                  ))}
                </Dropdown>
              </div>
            </div>

            {/* Titel */}
            <div className={styles.fieldRow}>
              <span className={styles.label}>Titel</span>
              <div className={styles.inputContainer}>
                <Select
                  id={dropdownIdTitel}
                  value={contactData?.titel ?? ""}
                  onChange={(e) =>
                    setContactData((cd) =>
                      cd ? { ...cd, titel: e.target.value } : cd
                    )
                  }
                  style={{ width: "100%" }}
                >
                  <option value="">- Kein Titel -</option>
                  {titelOptions.map((option) => (
                    <option key={option.id} value={String(option.id)}>
                      {option.title}
                    </option>
                  ))}
                </Select>
              </div>
            </div>

            {/* Geschlecht */}
            <div className={styles.fieldRow}>
              <span className={styles.label}>Geschlecht</span>
              <div className={styles.inputContainer}>
                <Dropdown
                  placeholder="Auswählen"
                  value={contactData?.geschlecht ?? ""}
                  selectedOptions={contactData?.geschlecht ? [contactData.geschlecht] : []}
                  onOptionSelect={(_, d) =>
                    setContactData((cd) =>
                      cd ? { ...cd, geschlecht: (d.optionValue ?? "") as string } : cd,
                    )
                  }
                  style={{ width: "100%" }}
                >
                  <Option value="Männlich">Männlich</Option>
                  <Option value="Weiblich">Weiblich</Option>
                  <Option value="Divers">Divers</Option>
                  <Option value="Ohne Angabe">Ohne Angabe</Option>
                </Dropdown>
              </div>
            </div>

            {/* Name */}
            <div className={styles.fieldRow}>
              <span className={styles.label}>Nachname</span>
              <div className={styles.inputContainer}>
                <Input
                  value={contactData?.lastname ?? ""}
                  placeholder="Nachname"
                  onChange={(_, data) =>
                    setContactData((cd) =>
                      cd ? { ...cd, lastname: data.value } : cd,
                    )
                  }
                  style={{ width: "100%" }}
                />
              </div>
            </div>

            {/* Vorname (gemäß Personalausweis) */}
            <div className={styles.fieldRow}>
              <span className={styles.label}>Vorname (gemäß Personalausweis)</span>
              <div className={styles.inputContainer}>
                <Input
                  value={contactData?.vorname ?? ""}
                  placeholder="Vorname (gemäß Personalausweis)"
                  onChange={(_, data) =>
                    setContactData((cd) =>
                      cd ? { ...cd, vorname: data.value, firstName: data.value } : cd,
                    )
                  }
                  style={{ width: "100%" }}
                />
              </div>
            </div>

            {/* Rufname */}
            <div className={styles.fieldRow}>
              <span className={styles.label}>Rufname</span>
              <div className={styles.inputContainer}>
                <Input
                  value={contactData?.rufname ?? ""}
                  placeholder="Rufname"
                  onChange={(_, data) =>
                    setContactData((cd) => (cd ? { ...cd, rufname: data.value } : cd))
                  }
                  style={{ width: "100%" }}
                />
              </div>
            </div>

            {/* Geburtsort */}
            <div className={styles.fieldRow}>
              <span className={styles.label}>Geburtsort</span>
              <div className={styles.inputContainer}>
                <Input
                  value={contactData?.geburtsort ?? ""}
                  placeholder="Geburtsort"
                  onChange={(_, data) => setContactData((cd) => cd ? { ...cd, geburtsort: data.value } : cd)}
                  style={{ width: "100%" }}
                />
              </div>
            </div>

            {/* Geburtsdatum */}
            <div className={styles.fieldRow}>
              <span className={styles.label}>Geburtsdatum</span>
              <div className={styles.inputContainer}>
                <Input
                  type="date"
                  value={contactData?.birthDate ?? ""}
                  placeholder="YYYY-MM-DD"
                  onChange={(_, data) =>
                    setContactData((cd) =>
                      cd ? { ...cd, birthDate: data.value } : cd,
                    )
                  }
                  style={{ width: "100%" }}
                />
              </div>
            </div>

            {/* Geburtsname */}
            <div className={styles.fieldRow}>
              <span className={styles.label}>Geburtsname</span>
              <div className={styles.inputContainer}>
                <Input
                  value={contactData?.geburtsname ?? ""}
                  placeholder="Geburtsname"
                  onChange={(_, data) => setContactData((cd) => cd ? { ...cd, geburtsname: data.value } : cd)}
                  style={{ width: "100%" }}
                />
              </div>
            </div>

            {/* Staatsangehörigkeit */}
            <div className={styles.fieldRow}>
              <span className={styles.label}>Staatsangehörigkeit</span>
              <div className={styles.inputContainer}>
                <Input
                  value={contactData?.staatsang ?? ""}
                  placeholder="Staatsangehörigkeit"
                  onChange={(_, data) => setContactData((cd) => cd ? { ...cd, staatsang: data.value } : cd)}
                  style={{ width: "100%" }}
                />
              </div>
            </div>

            {/* Anschrift */}
            <span
              className={styles.labelSection}
              style={{ display: "flex", alignItems: "center", gap: 8 }}
            >
              Anschrift{" "}
              <LayerDiagonal24Regular
                style={{ color: "purple", cursor: "pointer" }}
                onClick={() => { setDataModalTab('addresses'); setIsDataModalOpen(true); }}
              />
            </span>

            {/* Straße und Hausnummer */}
            <div className={styles.fieldRow}>
              <span className={styles.label}>Straße und Hausnummer</span>
              <div className={styles.inputContainer}>
                <Input
                  value={contactData?.strasse ?? ""}
                  placeholder="Straße und Hausnummer"
                  onChange={(_, data) => setContactData((cd) => cd ? { ...cd, strasse: data.value } : cd)}
                  style={{ width: "100%" }}
                />
              </div>
            </div>

            {/* PLZ */}
            <div className={styles.fieldRow}>
              <span className={styles.label}>PLZ</span>
              <div className={styles.inputContainer}>
                <Input
                  value={contactData?.plz ?? ""}
                  placeholder="PLZ"
                  onChange={(_, data) => setContactData((cd) => cd ? { ...cd, plz: data.value } : cd)}
                  style={{ width: "100%" }}
                />
              </div>
            </div>

            {/* Ort */}
            <div className={styles.fieldRow}>
              <span className={styles.label}>Ort</span>
              <div className={styles.inputContainer}>
                <Input
                  value={contactData?.ort ?? ""}
                  placeholder="Ort"
                  onChange={(_, data) => setContactData((cd) => cd ? { ...cd, ort: data.value } : cd)}
                  style={{ width: "100%" }}
                />
              </div>
            </div>

            {/* Ortsteil */}
            <div className={styles.fieldRow}>
              <span className={styles.label}>Ortsteil</span>
              <div className={styles.inputContainer}>
                <Input
                  value={contactData?.ortsteil ?? ""}
                  placeholder="Ortsteil"
                  onChange={(_, data) => setContactData((cd) => cd ? { ...cd, ortsteil: data.value } : cd)}
                  style={{ width: "100%" }}
                />
              </div>
            </div>

            {/* Kommunikation */}
            <span
              className={styles.labelSection}
              style={{ display: "flex", alignItems: "center", gap: 8 }}
            >
              Kommunikation{" "}
              <AvailabilityPopover
                contactId={Number(initialValues.kontaktId) || (contactData?.kontaktId ? Number(contactData.kontaktId) : 0)}
                initialValue={contactData?.erreichbarkeit}
                onUpdate={(val) => setContactData(cd => cd ? { ...cd, erreichbarkeit: val } : cd)}
              />
              <LayerDiagonal24Regular
                style={{ color: "purple", cursor: "pointer" }}
                onClick={() => { setDataModalTab('communications'); setIsDataModalOpen(true); }}
              />
            </span>

            <div style={{ display: "flex", gap: "10px", alignItems: "center", marginBottom: "15px" }}>
              {(['linkedin', 'instagram', 'facebook', 'tiktok', 'xing', 'youtube', 'homepage'] as const).map((platform) => {
                const existing = (contactData?.communications || []).find((c: any) => c.type === platform);
                return (
                  <SocialMediaIcon
                    key={platform}
                    platform={platform}
                    existing={existing}
                    contactId={Number(initialValues.kontaktId) || (contactData?.kontaktId ? Number(contactData.kontaktId) : 0)}
                    onSave={(newComm: any) => {
                      setContactData((prev: any) => {
                        if (!prev) return prev;
                        const comms = prev.communications || [];
                        const exists = comms.find((c: any) => c.id === newComm.id);
                        let newComms;
                        if (exists) {
                          newComms = comms.map((c: any) => c.id === newComm.id ? newComm : c);
                        } else {
                          newComms = [...comms, newComm];
                        }
                        return { ...prev, communications: newComms };
                      });
                    }}
                  />
                );
              })}
            </div>
            <div className={styles.fieldRow}></div>

            {/* Telefon privat / geschäftlich */}
            {renderCommRow("Telefon privat", "phone", "privat")}
            {renderCommRow("Telefon geschäftlich", "phone", "geschäftlich")}

            {/* Mobil privat / geschäftlich */}
            {renderCommRow("Mobil privat", "mobile", "privat")}
            {renderCommRow("Mobil geschäftlich", "mobile", "geschäftlich")}

            {/* Fax */}
            <div className={styles.fieldRow}>
              <span className={styles.label}>Telefax</span>
              <div className={styles.inputContainer}>
                <FluentTelefonInput
                  value={contactData?.communications?.find((c: any) => c.type === 'fax' && c.isDefault)?.value ?? ""}
                  placeholder="Telefax"
                  onChange={(faxWert) => setContactData(cd => {
                    if (!cd) return cd;
                    const comms = [...(cd.communications || [])];
                    const idx = comms.findIndex(c => c.type === 'fax' && c.isDefault);
                    if (idx !== -1) {
                      comms[idx] = { ...comms[idx], value: faxWert };
                    } else {
                      comms.push({ type: 'fax', value: faxWert, isDefault: true });
                    }
                    return { ...cd, communications: comms };
                  })}
                  style={{ width: "100%" }}
                />
              </div>
            </div>

            {/* Email privat / geschäftlich */}
            {renderCommRow("Email privat", "email", "privat")}
            {renderCommRow("Email geschäftlich", "email", "geschäftlich")}



            {/* Beruf */}
            <span
              className={styles.labelSection}
              style={{ display: "flex", alignItems: "center", gap: 8 }}
            >
              Beruf{" "}
            </span>

            {/* Beruf */}
            <div className={styles.fieldRow}>
              <span className={styles.label}>Status</span>
              <div className={styles.inputContainer}>
                <Dropdown
                  placeholder="Berufsstatus wählen"
                  value={contactData?.berufsstatus ?? ""}
                  selectedOptions={contactData?.berufsstatus ? [contactData.berufsstatus] : []}
                  onOptionSelect={(_, data) =>
                    setContactData((cd) => cd ? { ...cd, berufsstatus: data.optionValue ?? "" } : cd)
                  }
                  style={{ width: "100%" }}
                >
                  {withBerufsstatusValue(berufsstatusOptions, contactData?.berufsstatus).map((option) => (
                    <Option key={option} value={option}>
                      {option}
                    </Option>
                  ))}
                </Dropdown>
              </div>
            </div>
            <div className={styles.fieldRow}>
              <span className={styles.label}>Arbeitgeber</span>
              <div className={styles.inputContainer}>
                <Input
                  value={contactData?.arbeitgeber ?? ""}
                  placeholder="Arbeitgeber"
                  onChange={(_, data) => setContactData((cd) => cd ? { ...cd, arbeitgeber: data.value } : cd)}
                  style={{ width: "100%" }}
                />
              </div>
            </div>
            <div className={styles.fieldRow}>
              <span className={styles.label}>Branche</span>
              <div className={styles.inputContainer}>
                <Input
                  value={contactData?.branche ?? ""}
                  placeholder="Branche"
                  onChange={(_, data) => setContactData((cd) => cd ? { ...cd, branche: data.value } : cd)}
                  style={{ width: "100%" }}
                />
              </div>
            </div>
            <div className={styles.fieldRow}>
              <span className={styles.label}>Tätigkeit</span>
              <div className={styles.inputContainer}>
                <Input
                  value={contactData?.taetigkeit ?? ""}
                  placeholder="Tätigkeit"
                  onChange={(_, data) => setContactData((cd) => cd ? { ...cd, taetigkeit: data.value } : cd)}
                  style={{ width: "100%" }}
                />
              </div>
            </div>

            {/* Bank */}
            <span
              className={styles.labelSection}
              style={{ display: "flex", alignItems: "center", gap: 8 }}
            >
              Bank{" "}
              <LayerDiagonal24Regular
                style={{ color: "purple", cursor: "pointer" }}
                onClick={() => { setDataModalTab('banks'); setIsDataModalOpen(true); }}
              />
            </span>

            {/* Bank */}
            <div className={styles.fieldRow}>
              <span className={styles.label}>Bank</span>
              <div className={styles.inputContainer}>
                <Input
                  value={contactData?.defaultBankAccount?.bankName ?? contactData?.bank ?? ""}
                  placeholder="Bank"
                  onChange={(_, data) => setContactData(cd => cd ? { ...cd, bank: data.value } : cd)}
                  style={{ width: "100%" }}
                />
              </div>
            </div>

            {/* IBAN */}
            <div className={styles.fieldRow}>
              <span className={styles.label}>IBAN</span>
              <div className={styles.inputContainer}>
                <Input
                  value={contactData?.defaultBankAccount?.iban ?? contactData?.iban ?? ""}
                  placeholder="IBAN"
                  onChange={(_, data) => setContactData(cd => cd ? { ...cd, iban: data.value } : cd)}
                  style={{ width: "100%" }}
                />
              </div>
            </div>

            {/* BIC */}
            <div className={styles.fieldRow}>
              <span className={styles.label}>BIC</span>
              <div className={styles.inputContainer}>
                <Input
                  value={contactData?.defaultBankAccount?.bic ?? contactData?.bic ?? ""}
                  placeholder="BIC"
                  onChange={(_, data) => setContactData(cd => {
                    if (!cd) return cd;
                    return { ...cd, bic: data.value, defaultBankAccount: { ...(cd.defaultBankAccount || {}), bic: data.value } };
                  })}
                  style={{ width: "100%" }}
                />
              </div>
            </div>

            <div className={styles.fieldRow}>
              <span className={styles.label}>Zweck</span>
              <div className={styles.inputContainer}>
                <Input
                  value={contactData?.defaultBankAccount?.bankDescription ?? ""}
                  placeholder="Zweck"
                  onChange={(_, data) => setContactData(cd => {
                    if (!cd) return cd;
                    const db = cd.defaultBankAccount || {};
                    return { ...cd, defaultBankAccount: { ...db, bankDescription: data.value } };
                  })}
                  style={{ width: "100%" }}
                />
              </div>
            </div>

            {/* Kontoinhaber */}
            <div className={styles.fieldRow}>
              <span className={styles.label}>Kontoinhaber</span>
              <div className={styles.inputContainer}>
                <Input
                  value={contactData?.accountHolder ?? ""}
                  placeholder="Kontoinhaber"
                  onChange={(_, data) => setContactData(cd => cd ? { ...cd, accountHolder: data.value } : cd)}
                  style={{ width: "100%" }}
                />
              </div>
            </div>

            {/* Bankeinzug */}
            <div className={styles.fieldRow}>
              <span className={styles.label}>Bankeinzug</span>
              <div className={styles.inputContainer}>
                <Select
                  value={contactData?.directDebit ? "ja" : "nein"}
                  onChange={(_, data) => setContactData(cd => cd ? { ...cd, directDebit: data.value === "ja" } : cd)}
                  style={{ width: "100%" }}
                >
                  <option value="nein">Nein</option>
                  <option value="ja">Ja</option>
                </Select>
              </div>
            </div>


            {/* Sonstiges */}
            <span
              className={styles.labelSection}
              style={{ display: "flex", alignItems: "center", gap: 8 }}
            >
              Sonstiges{" "}
            </span>

            {/* Kontaktverantwortlicher FO (Benutzer) + BO (Team) */}
            {renderOwnershipFields()}

            {/* Familienstand */}
            <div className={styles.fieldRow}>
              <span className={styles.label}>Familienstand</span>
              <div className={styles.inputContainer}>
                <Select
                  value={contactData?.familienstand ?? ""}
                  onChange={(e) => setContactData(cd => cd ? { ...cd, familienstand: e.target.value } : cd)}
                  style={{ width: "100%" }}
                >
                  <option value="">Familienstand auswählen</option>
                  {familienstandOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                  {/* Frei eingegebene / Altdaten-Werte (familyStatusOther), die nicht
                      in der Standardliste stehen, sichtbar machen — sonst rendert das
                      Select leer und der Wert wirkt verloren. */}
                  {contactData?.familienstand &&
                    !familienstandOptions.some(
                      (o) => o.value === contactData.familienstand,
                    ) && (
                      <option value={contactData.familienstand}>
                        {contactData.familienstand}
                      </option>
                    )}
                </Select>
              </div>
            </div>

            {/* Raucher */}


            {/* Data Management Modal */}
            <DataManagementModal
              open={isDataModalOpen}
              contactId={initialValues.kontaktId}
              onClose={setIsDataModalOpen}
              contactName={contactData?.displayName || "Unbekannt"}
              addresses={contactData?.addresses ?? []}
              banks={contactData?.bankAccounts ?? []}
              communications={contactData?.communications ?? []}
              relations={contactData?.relationsSrc ?? []}
              initialTab={dataModalTab}
            />
          </div>
          <RelationshipDetails
            kontaktId={contactData?.kontaktId ?? Number(id)}
            relations={contactData?.relationsSrc ?? []}
            contactType={(contactData?.typ as string) ?? contactData?.type?.type}
            group={contactData?.group}
            onChanged={() => setRelReload((k) => k + 1)}
          />
        </div>
      )}
      {(() => {
        const resolvedKontaktId = id ? Number(id) : Number(initialValues.kontaktId);
        if (!resolvedKontaktId) return null;
        return (
          <>
            {selectedTab === "vertraege" && (
              <Vertragsaufstellung
                kontaktId={resolvedKontaktId}
                contactName={contactData?.displayName}
              />
            )}
            {selectedTab === "schaeden" && (
              <DamagesTableContact
                kontaktId={resolvedKontaktId}
                kontaktName={contactData?.displayName}
              />
            )}
            {selectedTab === "risiken" && (
              <>
                <ContactRisksPanel
                  contactId={resolvedKontaktId}
                  onSelectTab={(v) => setSelectedTab(v)}
                  contactTyp={(contactData?.typ ?? contactData?.type?.type) as string | undefined}
                  contactName={displayContactName(contactData)}
                />
              </>
            )}
            {selectedTab === "vorgaenge" && (
              <ContactVorgaengeTab contactId={resolvedKontaktId} />
            )}
            {selectedTab === "diary" && (
              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                <KundenTagebuch contactId={resolvedKontaktId} />
                <MergeFieldArchive contactId={resolvedKontaktId} />
                <TimelineView entityName="Contact" entityId={resolvedKontaktId} />
              </div>
            )}
            {selectedTab === "documents" && (
              <DocumentsSection contactId={resolvedKontaktId} />
            )}
            {selectedTab === "mail" && (
              <ContactMailView
                contactId={resolvedKontaktId}
                isCompany={contactData?.typ === "Firma" || contactData?.type?.type === "Firma"}
                emailAddresses={
                  (contactData?.communications || [])
                    .filter((c: any) => c.type === "email" && c.value)
                    .map((c: any) => c.value as string)
                }
              />
            )}
          </>
        );
      })()}
      {/* Notes Modal */}
      <Dialog open={isNotesModalOpen} onOpenChange={(event, data) => setIsNotesModalOpen(data.open)}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Schnell-Notiz</DialogTitle>
            <DialogContent>
              <Textarea placeholder="Notiz eingeben..." style={{ width: '100%', minHeight: '150px' }} />
            </DialogContent>
            <DialogActions>
              <DialogTrigger disableButtonEnhancement>
                <Button appearance="secondary">Schließen</Button>
              </DialogTrigger>
              <Button appearance="primary">Speichern</Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
      {/* Empfehlungsgeber Search Dialog */}
      <Dialog open={isEmpfehlungModalOpen} onOpenChange={(_, data) => setIsEmpfehlungModalOpen(data.open)}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Empfehlungsgeber suchen</DialogTitle>
            <DialogContent>
              <div style={{ display: "flex", gap: "8px", marginBottom: "16px" }}>
                <Input
                  value={empfehlungSearch}
                  onChange={(_, data) => setEmpfehlungSearch(data.value)}
                  placeholder="Name suchen..."
                  style={{ flex: 1 }}
                />
                <Button onClick={handleEmpfehlungSearch} disabled={isEmpfehlungLoading}>
                  {isEmpfehlungLoading ? "Lädt..." : "Suchen"}
                </Button>
              </div>
              <div style={{ maxHeight: "300px", overflowY: "auto" }}>
                {empfehlungResults.map((item) => (
                  <div
                    key={item.id}
                    onClick={() => selectEmpfehlung(item)}
                    style={{
                      padding: "8px",
                      borderBottom: "1px solid #eee",
                      cursor: "pointer",
                      display: "flex",
                      justifyContent: "space-between",
                    }}
                  >
                    <span>{item.displayName || `${item.lastname}, ${item.firstName}`}</span>
                    <ChevronRight20Regular />
                  </div>
                ))}
                {empfehlungResults.length === 0 && !isEmpfehlungLoading && empfehlungSearch && (
                  <div style={{ padding: "8px", color: "#666" }}>Keine Ergebnisse gefunden.</div>
                )}
              </div>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setIsEmpfehlungModalOpen(false)}>Abbrechen</Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
      <Button
        icon={<ChatEmptyRegular />}
        className={styles.floatingButton}
        onClick={() => setIsFloatingNotesOpen(true)}
      />

      <Dialog open={isFloatingNotesOpen} onOpenChange={(e, data) => setIsFloatingNotesOpen(data.open)}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Notizen</DialogTitle>
            <DialogContent>
              <Textarea
                value={floatingNotes}
                onChange={(e, data) => setFloatingNotes(data.value)}
                style={{ height: '300px', width: '100%', resize: 'none' }}
                placeholder="Notizen eingeben..."
              />
            </DialogContent>
            <DialogActions>
              <Button 
                  appearance="secondary" 
                  onClick={() =>
                      openVorgangCreateFlow(openWindow, {
                          initialContactId: contactData?.kontaktId ?? null,
                          initialContactName: [contactData?.lastname, contactData?.vorname].filter(Boolean).join(", ") || null,
                          initialSubject: `Notiz: ${contactData?.vorname ?? ""} ${contactData?.lastname ?? ""}`.trim(),
                      })
                  }
              >
                  Vorgang erstellen &gt;
              </Button>
              <Button appearance="primary" onClick={() => setIsFloatingNotesOpen(false)}>Schließen</Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      <Toaster toasterId={toasterId} />
    </div>
  );
};

export const saveContactDetails = async (data: ContactDetailsFormValues) => {
  await fetch(apiUrl(`contact/${data.kontaktId}`), {
    method: "PUT",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({
      lastname: data.lastname,
      vorname: data.vorname,
      postleitzahl: data.plz,
      ort: data.ort,
      strasse: data.strasse,
      legalForm: data.legalForm ?? data.gesellschaftsform ?? undefined,
      birthDate: data.birthDate, // <--- Ergänzen!
      defaultPhone:
        data.defaultPhone ??
        (data.telefon
          ? {
            id: undefined,
            type: "phone",
            label: "Haupt",
            value: data.telefon,
          }
          : undefined),
      defaultBankAccount: {
        bankName: data.defaultBankAccount?.bankName ?? data.bank ?? undefined,
        iban: data.defaultBankAccount?.iban ?? data.iban ?? undefined,
        bic: data.defaultBankAccount?.bic ?? data.bic ?? undefined,
      },
      // weitere Felder nach Bedarf
    }),
  });
};

export default ContactDetailsForm;
